import React, { useEffect, useState, useMemo, useRef } from 'react';
import { Container, Table, Button, Badge, Form, InputGroup, Dropdown, Modal, Spinner, Alert, Offcanvas, Tabs, Tab, Row, Col, ListGroup, Card, OverlayTrigger, Tooltip } from 'react-bootstrap';
import {
  useGetClientsQuery,
  useCreateClientMutation,
  useCreateContactMutation,
  useCreateLeadMutation,
  useCreateDealMutation,
  useUpdateClientMutation,
  useDeleteClientMutation,
  useGetMeetingsQuery,
  useCreateMeetingMutation,
  useGetEmailsQuery,
  useGetDealsQuery,
  useGetLeadsQuery,
  useGetContactsQuery,
  useGetRulesEngineConfigQuery,
  useGetUserPreferencesQuery,
  // Accounts hooks kept for future use; Contract Renewal is per-record.
  useGetAccountsQuery,
  useCreateAccountMutation,
  useUpdateAccountMutation,
} from '../services/api';
import { DEFAULT_RULES_ENGINE_CONFIG, normalizeRulesEngineConfig } from '../constants/rulesEngine';
import { buildEmailThreadsForEntity, mergeEmailResponses } from '../utils/crmEmailThreads';
import { useUser } from '../contexts/UserContext';
import { useUserPreferencesQueryArg } from '../hooks/useTenantIdForPreferences';
import {
  getDateKeyInTimezone,
  getRelativeDayDifferenceInTimezone,
  getTenantTimezoneFromPreferencesData,
  toLocaleDateStringInTimezone,
  toLocaleTimeStringInTimezone,
} from '../utils/tenantTimezone';
import { mergeLabelCatalogCandidates } from '../utils/crmLabels';
import {
  formatCompactRenewalLabelFromRawDate,
  formatHtmlDateValueToUsStorage,
  getDaysUntilContractRenewalDate,
  isContractRenewalOverdue,
  normalizeClientDateToUsStorage,
} from '../utils/crmContractRenewal';
import { 
  Search, 
  Plus, 
  Folder, 
  Lightning,
  ChevronDown, 
  ThreeDotsVertical, 
  Gear, 
  Building,
  X,
  Envelope,
  Phone,
  Tag,
  InfoCircle,
  Person,
  CurrencyDollar,
  Calendar,
  Clock,
  Briefcase,
  CheckSquare,
  ArrowRight,
  Dot,
  BuildingFill,
  GeoAltFill,
  Globe2,
  Linkedin,
  PeopleFill,
  FileText,
  TypeBold,
  TypeItalic,
  TypeUnderline,
  Link45deg,
  At,
  ListUl,
  ListOl,
  ArrowLeft,
  TypeStrikethrough,
  Pin,
  Paperclip,
  Inbox,
  Archive,
  Check,
  Trash,
  Star,
  StarFill,
  Funnel,
  Diamond,
  DiamondFill
} from 'react-bootstrap-icons';

interface Client {
  id: string;
  name: string;
  firstName?: string;
  lastName?: string;
  visibility?: 'global' | 'personal';
  owner?: string;
  accountId?: string;
  organization?: string;
  industry?: string;
  website?: string;
  address?: string;
  city?: string;
  state?: string;
  zipCode?: string;
  country?: string;
  phone?: string;
  email?: string;
  notes?: ClientNote[] | string;
  tags?: string[];
  status?: 'current' | 'past';
  mailboxStatus?: 'inbox' | 'archived';
  pastClientReason?: string;
  accountStage?: string;
  contactStage?: string;
  value?: string;
  expectedCloseDate?: string;
  contractRenewalDate?: string;
  businessUnit?: string;
  inStore?: boolean;
  sourceOrigin?: string;
  sourceChannel?: string;
  sourceChannelId?: string;
  linkedInProfile?: string;
  numberOfEmployees?: string;
  annualRevenue?: string;
  decisionMaker?: boolean;
  title?: string;
  workPhone?: string;
  cellPhone?: string;
  assistantName?: string;
  assistantPhone?: string;
  assistantEmail?: string;
  createdAt?: string;
  updatedAt?: string;
}

interface ClientNote {
  id: string;
  content: string;
  timestamp: string;
  author: string;
  pinned?: boolean;
  pinnedAt?: string;
}

interface ScheduledMeetingActivity {
  id: string;
  title: string;
  startTime: string;
  endTime: string;
  clientId?: string;
  meetingType?: 'in-person' | 'video' | 'phone';
  tags?: string[];
}

interface ActivityPurposeOption {
  id: string;
  name: string;
  color?: string;
}

interface DetailedActivityFormData {
  title: string;
  description: string;
  startTime: string;
  endTime: string;
  location: string;
  meetingType: 'in-person' | 'video' | 'phone';
  meetingUrl: string;
  clientId: string;
  contactId: string;
  dealId: string;
  leadId: string;
  purposeTypeId: string;
  notes: string;
  priority: 'low' | 'normal' | 'high';
  freeBusy: 'free' | 'busy';
  markAsDone: boolean;
  videoCallProvider: '' | 'zoom' | 'teams' | 'google-meet';
}

type ScheduleActivityType =
  | 'call'
  | 'text'
  | 'meeting'
  | 'task'
  | 'deadline'
  | 'email'
  | 'lunch';

type QuickSchedulePreset = 'in1h' | 'in3h' | 'tomorrow' | 'nextWeek' | 'other';

type ClientEditableFieldId =
  | 'name'
  | 'email'
  | 'phone'
  | 'value'
  | 'contactStage'
  | 'expectedCloseDate'
  | 'contractRenewalDate'
  | 'businessUnit'
  | 'inStore'
  | 'sourceOrigin'
  | 'sourceChannel'
  | 'sourceChannelId'
  | 'owner'
  | 'visibility'
  | 'organization'
  | 'accountStage'
  | 'address'
  | 'website'
  | 'linkedInProfile'
  | 'numberOfEmployees'
  | 'annualRevenue'
  | 'industry'
  | 'decisionMaker'
  | 'title'
  | 'workPhone'
  | 'cellPhone'
  | 'assistantName'
  | 'assistantPhone'
  | 'assistantEmail';

type LabelsFieldId = 'detailsLabels' | 'personLabels' | 'organizationLabels';
type OwnerFilterPanelTab = 'favorites' | 'owners' | 'filters';

type ClientEditableFieldConfig = {
  clientKey: keyof Client;
  inputType: 'text' | 'date' | 'checkbox';
  placeholder: string;
};

const CLIENT_EDITABLE_FIELD_CONFIG: Record<ClientEditableFieldId, ClientEditableFieldConfig> = {
  name: { clientKey: 'name', inputType: 'text', placeholder: 'No name' },
  email: { clientKey: 'email', inputType: 'text', placeholder: 'No email' },
  phone: { clientKey: 'phone', inputType: 'text', placeholder: 'No phone' },
  value: { clientKey: 'value', inputType: 'text', placeholder: '$ Value' },
  contactStage: { clientKey: 'contactStage', inputType: 'text', placeholder: 'Contact Stage' },
  expectedCloseDate: { clientKey: 'expectedCloseDate', inputType: 'date', placeholder: 'Expected close date' },
  contractRenewalDate: { clientKey: 'contractRenewalDate', inputType: 'date', placeholder: 'Contract Renewal' },
  businessUnit: { clientKey: 'businessUnit', inputType: 'text', placeholder: 'Business Unit' },
  inStore: { clientKey: 'inStore', inputType: 'checkbox', placeholder: 'In-Store' },
  sourceOrigin: { clientKey: 'sourceOrigin', inputType: 'text', placeholder: 'Source origin' },
  sourceChannel: { clientKey: 'sourceChannel', inputType: 'text', placeholder: 'Source channel' },
  sourceChannelId: { clientKey: 'sourceChannelId', inputType: 'text', placeholder: 'Source channel ID' },
  owner: { clientKey: 'owner', inputType: 'text', placeholder: 'Owner' },
  visibility: { clientKey: 'visibility', inputType: 'text', placeholder: 'Visibility' },
  organization: { clientKey: 'organization', inputType: 'text', placeholder: 'No organization' },
  accountStage: { clientKey: 'accountStage', inputType: 'text', placeholder: 'Account Stage' },
  address: { clientKey: 'address', inputType: 'text', placeholder: 'Address' },
  website: { clientKey: 'website', inputType: 'text', placeholder: 'Website' },
  linkedInProfile: { clientKey: 'linkedInProfile', inputType: 'text', placeholder: 'LinkedIn profile' },
  numberOfEmployees: { clientKey: 'numberOfEmployees', inputType: 'text', placeholder: 'Number of employees' },
  annualRevenue: { clientKey: 'annualRevenue', inputType: 'text', placeholder: 'Annual revenue' },
  industry: { clientKey: 'industry', inputType: 'text', placeholder: 'Industry' },
  decisionMaker: { clientKey: 'decisionMaker', inputType: 'checkbox', placeholder: 'Decision Maker' },
  title: { clientKey: 'title', inputType: 'text', placeholder: 'Title' },
  workPhone: { clientKey: 'workPhone', inputType: 'text', placeholder: 'Work phone' },
  cellPhone: { clientKey: 'cellPhone', inputType: 'text', placeholder: 'Cell phone' },
  assistantName: { clientKey: 'assistantName', inputType: 'text', placeholder: "Assistant's Name" },
  assistantPhone: { clientKey: 'assistantPhone', inputType: 'text', placeholder: "Assistant's Phone" },
  assistantEmail: { clientKey: 'assistantEmail', inputType: 'text', placeholder: "Assistant's Email" },
};

const detailFieldIconStyle: React.CSSProperties = {
  width: '14px',
  height: '14px',
  display: 'inline-flex',
  alignItems: 'center',
  justifyContent: 'center',
  flexShrink: 0
};

const getSharedLabelColors = (): Record<string, string> => {
  const defaultColors: Record<string, string> = {
    HOT: 'danger',
    COLD: 'primary',
    WARM: 'warning',
    'QUARTER 2': 'secondary',
    'QUARTER 1': 'info',
    'QUARTER 3': 'info',
    'QUARTER 4': 'warning',
    NTTM: 'info',
    FRANCHISE: 'success',
    '1-10 LOCATIONS': 'warning',
    CHAINS: 'success',
    'I.P.M.': 'secondary'
  };

  try {
    const stored = localStorage.getItem('labelColors');
    if (stored) {
      const storedColors = JSON.parse(stored);
      return { ...defaultColors, ...storedColors };
    }
  } catch {
    // ignore storage parsing issues and fall back to defaults
  }

  return defaultColors;
};

const SCHEDULE_ACTIVITY_LABELS: Record<ScheduleActivityType, string> = {
  call: 'Call',
  text: 'Text',
  meeting: 'Meeting',
  task: 'Task',
  deadline: 'Deadline',
  email: 'Email',
  lunch: 'Lunch',
};

const SCHEDULE_ACTIVITY_OPTIONS: Array<{ type: ScheduleActivityType; label: string }> = [
  { type: 'call', label: 'Call' },
  { type: 'text', label: 'Text' },
  { type: 'meeting', label: 'Meeting' },
  { type: 'task', label: 'Task' },
  { type: 'deadline', label: 'Deadline' },
  { type: 'email', label: 'Email' },
  { type: 'lunch', label: 'Lunch' },
];

const DEFAULT_ACTIVITY_PURPOSE_OPTIONS: ActivityPurposeOption[] = [
  { id: 'internal-sales-meeting', name: 'Internal Sales Meeting', color: '#ff8c42' },
  { id: 'internal-developer-meetings', name: 'Internal Developer Meetings', color: '#6f42c1' },
  { id: 'client-sales-meetings', name: 'Client Sales Meetings', color: '#0d6efd' },
];

const DEFAULT_ACTIVITY_PURPOSE_ID = 'client-sales-meetings';
const ACTIVITY_CALENDAR_VISIBLE_HOURS = 12;
const ACTIVITY_CALENDAR_SLOT_HEIGHT = 30;
const ACTIVITY_CALENDAR_PANEL_WIDTH = 320;
const DEFAULT_OWNER_FILTER_LABEL = 'Everyone';
const DEFAULT_SELF_OWNER_LABEL = 'PhiLL Kross';
const CLIENT_OWNER_FAVORITES_STORAGE_KEY = 'clientOwnerFavorites';

const normalizeOwnerKey = (value: string): string =>
  String(value || '')
    .replace(/\(\s*you\s*\)/gi, '')
    .trim()
    .toLowerCase();

const formatActivityCalendarHourLabel = (hour: number): string => {
  const normalizedHour = ((hour % 24) + 24) % 24;
  const meridiem = normalizedHour >= 12 ? 'PM' : 'AM';
  const twelveHour = normalizedHour % 12 === 0 ? 12 : normalizedHour % 12;
  return `${twelveHour} ${meridiem}`;
};

const IconOnlyDropdownToggle = React.forwardRef<
  HTMLButtonElement,
  React.ButtonHTMLAttributes<HTMLButtonElement>
>(({ children, onClick, className, style, ...rest }, ref) => (
  <button
    ref={ref}
    type="button"
    className={`btn btn-outline-secondary btn-sm ${className || ''}`.trim()}
    onClick={(event) => {
      event.preventDefault();
      onClick?.(event);
    }}
    style={{
      width: '32px',
      height: '32px',
      padding: 0,
      display: 'inline-flex',
      alignItems: 'center',
      justifyContent: 'center',
      borderColor: '#ced4da',
      ...style
    }}
    {...rest}
  >
    {children}
  </button>
));
IconOnlyDropdownToggle.displayName = 'IconOnlyDropdownToggle';

const Clients: React.FC = () => {
  const { currentUser } = useUser();
  const [selectedStatus, setSelectedStatus] = useState<'current' | 'past' | ''>('');
  const { data: clientsData, isLoading: isLoadingClients, error: clientsError, refetch: refetchClients } = useGetClientsQuery(
    selectedStatus ? { status: selectedStatus } : {}
  );
  const { data: meetingsData, refetch: refetchMeetings } = useGetMeetingsQuery({});
  const { data: rulesEngineConfigData } = useGetRulesEngineConfigQuery(undefined);
  const userPreferencesQueryArg = useUserPreferencesQueryArg();
  const { data: userPreferencesData } = useGetUserPreferencesQuery(userPreferencesQueryArg);
  const tenantTimezone = useMemo(
    () => getTenantTimezoneFromPreferencesData(userPreferencesData),
    [userPreferencesData]
  );
  const timezoneOption = tenantTimezone ? { timeZone: tenantTimezone } : {};
  const [createClient, { isLoading: isCreatingClient }] = useCreateClientMutation();
  const [createContact] = useCreateContactMutation();
  const [createLead] = useCreateLeadMutation();
  const [createDeal] = useCreateDealMutation();
  const [createMeeting, { isLoading: isCreatingMeeting }] = useCreateMeetingMutation();
  // Accounts still load for other CRM features, but Contract Renewal is per-record.
  const { data: accountsData, refetch: refetchAccounts } = useGetAccountsQuery({});
  const [createAccount] = useCreateAccountMutation();
  const [updateAccount] = useUpdateAccountMutation();

  const getDaysUntilContractRenewal = (client: Client): number | null =>
    getDaysUntilContractRenewalDate(client.contractRenewalDate);
  const [updateClient, { isLoading: isUpdatingClient }] = useUpdateClientMutation();
  const [deleteClient, { isLoading: isDeletingClient }] = useDeleteClientMutation();
  
  const clients = clientsData || [];
  const accounts = accountsData || [];
  const accountNameMap = useMemo(() => {
    const map = new Map<string, any>();
    (accounts || []).forEach((account: any) => {
      const name = String(account?.name || '').trim();
      if (!name) return;
      map.set(name.toLowerCase(), account);
    });
    return map;
  }, [accounts]);
  const meetings = meetingsData || [];
  const rulesEngineConfig = useMemo(
    () => normalizeRulesEngineConfig(rulesEngineConfigData || DEFAULT_RULES_ENGINE_CONFIG),
    [rulesEngineConfigData]
  );
  const accountStageOptions = rulesEngineConfig.accountStages;
  const contactStageOptions = rulesEngineConfig.contactStages;
  
  const [selectedClients, setSelectedClients] = useState<Set<string>>(new Set());
  const [selectedClient, setSelectedClient] = useState<Client | null>(null);
  const [isConvertingClient, setIsConvertingClient] = useState(false);
  const [showSidebar, setShowSidebar] = useState(false);
  const [activeTab, setActiveTab] = useState('notes');
  const clientEmailAddress = String(selectedClient?.email || '').trim().toLowerCase();
  const shouldLoadClientEmailThreads = showSidebar && activeTab === 'email' && clientEmailAddress.length > 0;
  const {
    data: clientInboxEmailThreadsData,
    isLoading: isLoadingClientInboxEmailThreads,
    isFetching: isRefreshingClientInboxEmailThreads,
    error: clientInboxEmailThreadsError,
    refetch: refetchClientInboxEmailThreads
  } = useGetEmailsQuery(
    {
      emailAccount: 'all',
      folder: 'inbox',
      limit: 200,
    },
    {
      skip: !shouldLoadClientEmailThreads,
      refetchOnMountOrArgChange: true,
    }
  );
  const {
    data: clientSentEmailThreadsData,
    isLoading: isLoadingClientSentEmailThreads,
    isFetching: isRefreshingClientSentEmailThreads,
    error: clientSentEmailThreadsError,
    refetch: refetchClientSentEmailThreads
  } = useGetEmailsQuery(
    {
      emailAccount: 'all',
      folder: 'sent',
      limit: 200,
    },
    {
      skip: !shouldLoadClientEmailThreads,
      refetchOnMountOrArgChange: true,
    }
  );
  const clientEmailThreadsData = useMemo(
    () => mergeEmailResponses(clientInboxEmailThreadsData, clientSentEmailThreadsData),
    [clientInboxEmailThreadsData, clientSentEmailThreadsData]
  );
  const isLoadingClientEmailThreads = isLoadingClientInboxEmailThreads || isLoadingClientSentEmailThreads;
  const isRefreshingClientEmailThreads = isRefreshingClientInboxEmailThreads || isRefreshingClientSentEmailThreads;
  const clientEmailThreadsError = clientInboxEmailThreadsError && clientSentEmailThreadsError ? clientInboxEmailThreadsError : null;
  const refetchClientEmailThreads = (): void => {
    refetchClientInboxEmailThreads();
    refetchClientSentEmailThreads();
  };
  const [searchQuery, setSearchQuery] = useState('');
  const [showAddClientModal, setShowAddClientModal] = useState(false);
  const [showCustomizeColumnsModal, setShowCustomizeColumnsModal] = useState(false);
  const [showTagsFilter, setShowTagsFilter] = useState(false);
  const [showOwnerFilter, setShowOwnerFilter] = useState(false);
  const [ownerFilterTab, setOwnerFilterTab] = useState<OwnerFilterPanelTab>('owners');
  const [ownerFilterSearchQuery, setOwnerFilterSearchQuery] = useState('');
  const [hoveredOwnerRowKey, setHoveredOwnerRowKey] = useState<string | null>(null);
  const [selectedOwner, setSelectedOwner] = useState<string>(DEFAULT_OWNER_FILTER_LABEL);
  const [favoriteOwnerKeys, setFavoriteOwnerKeys] = useState<string[]>(() => {
    try {
      const stored = localStorage.getItem(CLIENT_OWNER_FAVORITES_STORAGE_KEY);
      const parsed = stored ? JSON.parse(stored) : [];
      return Array.isArray(parsed) ? parsed.map((entry) => normalizeOwnerKey(String(entry || ''))).filter(Boolean) : [];
    } catch {
      return [];
    }
  });
  const [selectedTag, setSelectedTag] = useState<string>('');
  const [labelsFilterSearchQuery, setLabelsFilterSearchQuery] = useState('');
  const [contractRenewalFilter, setContractRenewalFilter] = useState<'all' | 'overdue' | 'next30' | 'next60' | 'next90'>('all');
  const [clientMailboxView, setClientMailboxView] = useState<'inbox' | 'archive'>('inbox');
  const [showAllFields, setShowAllFields] = useState(false);
  const [showAllPersonFields, setShowAllPersonFields] = useState(false);
  const [showAllOrganizationFields, setShowAllOrganizationFields] = useState(false);
  // Contract renewal now uses standard detail-field editing flow via `contractRenewalDate`.
  const [hoveredField, setHoveredField] = useState<string | null>(null);
  const [editingDetailFieldId, setEditingDetailFieldId] = useState<string | null>(null);
  const [detailFieldDraftValue, setDetailFieldDraftValue] = useState('');
  const [detailFieldDraftChecked, setDetailFieldDraftChecked] = useState(false);
  const [showLabelsDropdownField, setShowLabelsDropdownField] = useState<LabelsFieldId | null>(null);
  const [labelSearchQuery, setLabelSearchQuery] = useState('');
  const [customLabels, setCustomLabels] = useState<string[]>(() => {
    try {
      const stored = localStorage.getItem('customLabels');
      return stored ? JSON.parse(stored) : [];
    } catch {
      return [];
    }
  });
  const noteEditorRef = useRef<HTMLDivElement | null>(null);
  const [isNoteEditorFocused, setIsNoteEditorFocused] = useState(false);
  const [currentNoteContent, setCurrentNoteContent] = useState('');
  const [notes, setNotes] = useState<ClientNote[]>([]);
  const [scheduledActivityType, setScheduledActivityType] = useState<ScheduleActivityType>('call');
  const [scheduledActivityText, setScheduledActivityText] = useState(SCHEDULE_ACTIVITY_LABELS.call);
  const [showScheduledActivityTypeDropdown, setShowScheduledActivityTypeDropdown] = useState(false);
  const [showActivityCalendar, setShowActivityCalendar] = useState(false);
  const [showCalendarToggleTooltip, setShowCalendarToggleTooltip] = useState(false);
  const [activityNowTick, setActivityNowTick] = useState(() => Date.now());
  const [activitySchedulerForm, setActivitySchedulerForm] = useState<DetailedActivityFormData>(() => {
    const now = new Date();
    const end = new Date(now.getTime() + 60 * 60 * 1000);
    const pad = (n: number) => String(n).padStart(2, '0');
    const toLocal = (d: Date) =>
      `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
    return {
      title: 'Call',
      description: '',
      startTime: toLocal(now),
      endTime: toLocal(end),
      location: '',
      meetingType: 'phone',
      meetingUrl: '',
      clientId: '',
      contactId: '',
      dealId: '',
      leadId: '',
      purposeTypeId: DEFAULT_ACTIVITY_PURPOSE_ID,
      notes: '',
      priority: 'normal',
      freeBusy: 'free',
      markAsDone: false,
      videoCallProvider: '',
    };
  });
  const [activityDealLeadSearch, setActivityDealLeadSearch] = useState('');
  const [showActivityDealLeadDropdown, setShowActivityDealLeadDropdown] = useState(false);
  const [activityPeopleSearch, setActivityPeopleSearch] = useState('');
  const [showActivityPeopleDropdown, setShowActivityPeopleDropdown] = useState(false);
  const [activityOrganizationSearch, setActivityOrganizationSearch] = useState('');
  const [showActivityOrganizationDropdown, setShowActivityOrganizationDropdown] = useState(false);
  const selfOwnerBaseLabel = useMemo(() => {
    const explicitName = String(currentUser?.name || '').trim();
    const combinedName = `${String(currentUser?.firstName || '').trim()} ${String(currentUser?.lastName || '').trim()}`.trim();
    return explicitName || combinedName || DEFAULT_SELF_OWNER_LABEL;
  }, [currentUser?.firstName, currentUser?.lastName, currentUser?.name]);
  const selfOwnerFilterLabel = useMemo(
    () => `${selfOwnerBaseLabel} (you)`,
    [selfOwnerBaseLabel]
  );
  const detailOwnerOptions = useMemo(
    () => [DEFAULT_OWNER_FILTER_LABEL, selfOwnerBaseLabel],
    [selfOwnerBaseLabel]
  );

  const [formData, setFormData] = useState({
    firstName: '',
    lastName: '',
    organization: '',
    accountStage: '',
    contactStage: '',
    visibility: 'global' as 'global' | 'personal',
    industry: '',
    website: '',
    address: '',
    city: '',
    state: '',
    zipCode: '',
    country: '',
    phone: '',
    email: '',
    notes: '',
    tags: '',
    status: 'current' as 'current' | 'past',
    pastClientReason: '',
    decisionMaker: false
  });

  const shouldLoadActivitySchedulerLookups = showSidebar && activeTab === 'activity' && !!selectedClient;
  const { data: dealsData } = useGetDealsQuery({}, { skip: !shouldLoadActivitySchedulerLookups });
  const { data: leadsData } = useGetLeadsQuery({}, { skip: !shouldLoadActivitySchedulerLookups });
  const { data: contactsData } = useGetContactsQuery({}, { skip: !shouldLoadActivitySchedulerLookups });

  const allTags = useMemo(() => {
    const tagSet = new Set<string>();
    clients.forEach((client: Client) => {
      if (client.tags && Array.isArray(client.tags)) {
        client.tags.forEach(tag => tagSet.add(tag));
      }
    });
    return Array.from(tagSet).sort();
  }, [clients]);

  const allAvailableLabels = useMemo(() => {
    const candidates: string[] = [
      ...Object.keys(getSharedLabelColors()),
      ...allTags,
      ...customLabels,
    ];
    return mergeLabelCatalogCandidates(candidates);
  }, [allTags, customLabels]);

  const meetingPurposeOptions = useMemo<ActivityPurposeOption[]>(() => {
    const persisted = (userPreferencesData as any)?.preferences?.crmMeetingPurposeTypes;
    if (!Array.isArray(persisted)) return DEFAULT_ACTIVITY_PURPOSE_OPTIONS;

    const seen = new Set<string>();
    const normalized: ActivityPurposeOption[] = [];
    persisted.forEach((item: any) => {
      if (!item || typeof item !== 'object') return;
      const name = String(item.name || item.label || '').trim().replace(/\s+/g, ' ');
      if (!name) return;
      const id = String(item.id || name)
        .trim()
        .toLowerCase()
        .replace(/[^a-z0-9]+/g, '-')
        .replace(/^-+|-+$/g, '');
      if (!id || seen.has(id)) return;
      seen.add(id);
      normalized.push({
        id,
        name,
        color: String(item.color || '').trim() || undefined,
      });
    });

    return normalized.length > 0 ? normalized : DEFAULT_ACTIVITY_PURPOSE_OPTIONS;
  }, [userPreferencesData]);

  useEffect(() => {
    if (meetingPurposeOptions.length === 0) return;
    setActivitySchedulerForm((prev) => {
      if (meetingPurposeOptions.some((option) => option.id === prev.purposeTypeId)) return prev;
      return {
        ...prev,
        purposeTypeId: meetingPurposeOptions[0].id,
      };
    });
  }, [meetingPurposeOptions]);

  const activityDealRecords = useMemo(
    () => (Array.isArray(dealsData) ? (dealsData as Array<{ id: string; name?: string }>) : []),
    [dealsData]
  );
  const activityLeadRecords = useMemo(
    () => (
      Array.isArray(leadsData)
        ? (leadsData as Array<{ id: string; name?: string; organization?: string }>)
        : []
    ),
    [leadsData]
  );
  const activityContactRecords = useMemo(
    () => (
      Array.isArray(contactsData)
        ? (contactsData as Array<{ id: string; firstName?: string; lastName?: string; organization?: string; email?: string }>)
        : []
    ),
    [contactsData]
  );
  const activityClientRecords = useMemo(
    () => (Array.isArray(clients) ? (clients as Array<{ id: string; name?: string; organization?: string }>) : []),
    [clients]
  );

  const filteredActivityDealsAndLeads = useMemo(() => {
    const query = activityDealLeadSearch.toLowerCase().trim();
    const dealItems = activityDealRecords.map((deal) => ({
      id: deal.id,
      type: 'deal' as const,
      label: deal.name || 'Untitled deal',
    }));
    const leadItems = activityLeadRecords.map((lead) => ({
      id: lead.id,
      type: 'lead' as const,
      label: lead.organization ? `${lead.name || 'Untitled'} (${lead.organization})` : (lead.name || 'Untitled lead'),
    }));
    const allItems = [...dealItems, ...leadItems];
    if (!query) return allItems.slice(0, 12);
    return allItems.filter((item) => item.label.toLowerCase().includes(query)).slice(0, 12);
  }, [activityDealRecords, activityLeadRecords, activityDealLeadSearch]);

  const activityDealLeadDisplayLabel = useMemo(() => {
    if (activitySchedulerForm.dealId) {
      const deal = activityDealRecords.find((item) => item.id === activitySchedulerForm.dealId);
      return deal?.name || 'Deal';
    }
    if (activitySchedulerForm.leadId) {
      const lead = activityLeadRecords.find((item) => item.id === activitySchedulerForm.leadId);
      if (!lead) return 'Lead';
      return lead.organization ? `${lead.name || ''} (${lead.organization})` : (lead.name || 'Lead');
    }
    return '';
  }, [activitySchedulerForm.dealId, activitySchedulerForm.leadId, activityDealRecords, activityLeadRecords]);

  const filteredActivityContacts = useMemo(() => {
    const query = activityPeopleSearch.toLowerCase().trim();
    const list = activityContactRecords.map((contact) => {
      const fullName = `${contact.firstName || ''} ${contact.lastName || ''}`.trim();
      const label = contact.organization ? `${fullName} (${contact.organization})` : (fullName || 'Untitled contact');
      const searchText = `${fullName} ${contact.organization || ''} ${contact.email || ''}`.toLowerCase();
      return { id: contact.id, label, searchText };
    });
    if (!query) return list.slice(0, 12).map(({ id, label }) => ({ id, label }));
    return list.filter((item) => item.searchText.includes(query)).slice(0, 12).map(({ id, label }) => ({ id, label }));
  }, [activityContactRecords, activityPeopleSearch]);

  const activityContactDisplayLabel = useMemo(() => {
    if (!activitySchedulerForm.contactId) return '';
    const contact = activityContactRecords.find((item) => item.id === activitySchedulerForm.contactId);
    if (!contact) return 'Contact';
    const fullName = `${contact.firstName || ''} ${contact.lastName || ''}`.trim();
    return contact.organization ? `${fullName} (${contact.organization})` : (fullName || 'Contact');
  }, [activitySchedulerForm.contactId, activityContactRecords]);

  const filteredActivityOrganizations = useMemo(() => {
    const query = activityOrganizationSearch.toLowerCase().trim();
    const list = activityClientRecords.map((client) => {
      const label = client.name || client.organization || 'Organization';
      return {
        id: client.id,
        label,
        searchText: `${label}`.toLowerCase(),
      };
    });
    if (!query) return list.slice(0, 12);
    return list.filter((item) => item.searchText.includes(query)).slice(0, 12);
  }, [activityClientRecords, activityOrganizationSearch]);

  const activityOrganizationDisplayLabel = useMemo(() => {
    if (!activitySchedulerForm.clientId) return '';
    const client = activityClientRecords.find((item) => item.id === activitySchedulerForm.clientId);
    return client?.name || client?.organization || 'Organization';
  }, [activitySchedulerForm.clientId, activityClientRecords]);

  const parseClientNotes = (rawNotes: unknown): ClientNote[] => {
    if (!Array.isArray(rawNotes)) return [];

    return rawNotes
      .map((note, index) => {
        if (!note || typeof note !== 'object') return null;
        const noteRecord = note as Partial<ClientNote>;
        const content = String(noteRecord.content || '').trim();
        const timestamp = String(noteRecord.timestamp || '').trim();
        if (!content || !timestamp) return null;

        return {
          id: String(noteRecord.id || `note-${timestamp}-${index}`),
          content: String(noteRecord.content),
          timestamp,
          author: String(noteRecord.author || 'PhiLL Kross'),
          pinned: Boolean(noteRecord.pinned),
          pinnedAt: String(noteRecord.pinnedAt || '').trim() || undefined,
        } as ClientNote;
      })
      .filter((note): note is ClientNote => Boolean(note))
      .sort((a, b) => new Date(b.timestamp).getTime() - new Date(a.timestamp).getTime());
  };

  const isClientArchived = (client?: Client | null): boolean =>
    String(client?.mailboxStatus || '').trim().toLowerCase() === 'archived';

  const ownerOptions = useMemo(() => {
    const ownerMap = new Map<string, string>();
    ownerMap.set(normalizeOwnerKey(DEFAULT_OWNER_FILTER_LABEL), DEFAULT_OWNER_FILTER_LABEL);
    ownerMap.set(normalizeOwnerKey(selfOwnerFilterLabel), selfOwnerFilterLabel);

    clients.forEach((client: Client) => {
      const ownerLabel = String(client.owner || '').trim();
      if (!ownerLabel) return;
      ownerMap.set(normalizeOwnerKey(ownerLabel), ownerLabel);
    });

    return Array.from(ownerMap.entries())
      .map(([key, label]) => ({ key, label }))
      .sort((a, b) => {
        if (a.key === normalizeOwnerKey(DEFAULT_OWNER_FILTER_LABEL)) return -1;
        if (b.key === normalizeOwnerKey(DEFAULT_OWNER_FILTER_LABEL)) return 1;
        return a.label.localeCompare(b.label);
      });
  }, [clients, selfOwnerFilterLabel]);

  const selectedOwnerButtonLabel = useMemo(() => {
    const normalizedSelected = normalizeOwnerKey(selectedOwner);
    if (!selectedOwner || normalizedSelected === normalizeOwnerKey(DEFAULT_OWNER_FILTER_LABEL)) {
      return DEFAULT_OWNER_FILTER_LABEL;
    }
    return String(selectedOwner).replace(/\(\s*you\s*\)/i, '').trim() || DEFAULT_OWNER_FILTER_LABEL;
  }, [selectedOwner]);

  const normalizedOwnerFilterSearchQuery = ownerFilterSearchQuery.trim().toLowerCase();

  const visibleOwnerOptions = useMemo(() => {
    if (!normalizedOwnerFilterSearchQuery) return ownerOptions;
    return ownerOptions.filter((option) => option.label.toLowerCase().includes(normalizedOwnerFilterSearchQuery));
  }, [ownerOptions, normalizedOwnerFilterSearchQuery]);

  const favoriteOwnerOptions = useMemo(() => {
    if (!favoriteOwnerKeys.length) return [];
    const favoriteKeySet = new Set(favoriteOwnerKeys.map((entry) => normalizeOwnerKey(entry)));
    return ownerOptions.filter((option) => favoriteKeySet.has(option.key));
  }, [ownerOptions, favoriteOwnerKeys]);

  const visibleFavoriteOwnerOptions = useMemo(() => {
    if (!normalizedOwnerFilterSearchQuery) return favoriteOwnerOptions;
    return favoriteOwnerOptions.filter((option) => option.label.toLowerCase().includes(normalizedOwnerFilterSearchQuery));
  }, [favoriteOwnerOptions, normalizedOwnerFilterSearchQuery]);

  const filteredClients = useMemo(() => {
    let filtered = [...clients];

    if (clientMailboxView === 'archive') {
      filtered = filtered.filter((client: Client) => isClientArchived(client));
    } else {
      filtered = filtered.filter((client: Client) => !isClientArchived(client));
    }

    if (searchQuery) {
      const query = searchQuery.toLowerCase();
      filtered = filtered.filter((client: Client) => {
        const name = (client.name || '').toLowerCase();
        const org = (client.organization || '').toLowerCase();
        const email = (client.email || '').toLowerCase();
        const phone = (client.phone || '').toLowerCase();
        return name.includes(query) || org.includes(query) || email.includes(query) || phone.includes(query);
      });
    }

    if (selectedTag) {
      filtered = filtered.filter((client: Client) => 
        Array.isArray(client.tags)
        && client.tags.some((tag) => String(tag).trim().toLowerCase() === selectedTag.trim().toLowerCase())
      );
    }

    const normalizedSelectedOwner = normalizeOwnerKey(selectedOwner);
    if (normalizedSelectedOwner && normalizedSelectedOwner !== normalizeOwnerKey(DEFAULT_OWNER_FILTER_LABEL)) {
      filtered = filtered.filter((client: Client) => {
        const ownerLabel = String(client.owner || '').trim();
        if (ownerLabel) return normalizeOwnerKey(ownerLabel) === normalizedSelectedOwner;
        if (normalizedSelectedOwner === normalizeOwnerKey(selfOwnerFilterLabel)) {
          return String(client.visibility || '').toLowerCase() !== 'global';
        }
        return false;
      });
    }

    if (contractRenewalFilter !== 'all') {
      filtered = filtered.filter((client: Client) => {
        const days = getDaysUntilContractRenewal(client);
        if (days === null) return false;
        if (contractRenewalFilter === 'overdue') return days < 0;
        if (contractRenewalFilter === 'next30') return days >= 0 && days <= 30;
        if (contractRenewalFilter === 'next60') return days >= 0 && days <= 60;
        if (contractRenewalFilter === 'next90') return days >= 0 && days <= 90;
        return true;
      });
    }

    if (contractRenewalFilter !== 'all') {
      filtered.sort((a: Client, b: Client) => {
        const aDays = getDaysUntilContractRenewal(a);
        const bDays = getDaysUntilContractRenewal(b);
        if (aDays === null && bDays === null) return 0;
        if (aDays === null) return 1;
        if (bDays === null) return -1;
        return aDays - bDays;
      });
    }

    return filtered;
  }, [clients, clientMailboxView, searchQuery, selectedTag, selectedOwner, selfOwnerFilterLabel, contractRenewalFilter]);

  useEffect(() => {
    try {
      localStorage.setItem(CLIENT_OWNER_FAVORITES_STORAGE_KEY, JSON.stringify(favoriteOwnerKeys));
    } catch {
      // Ignore persistence errors in private browsing modes.
    }
  }, [favoriteOwnerKeys]);

  useEffect(() => {
    if (normalizeOwnerKey(selectedOwner) === normalizeOwnerKey(DEFAULT_OWNER_FILTER_LABEL)) return;
    const selectedExists = ownerOptions.some((option) => option.key === normalizeOwnerKey(selectedOwner));
    if (!selectedExists) {
      setSelectedOwner(DEFAULT_OWNER_FILTER_LABEL);
    }
  }, [ownerOptions, selectedOwner]);

  const toggleFavoriteOwner = (ownerKey: string) => {
    const normalizedKey = normalizeOwnerKey(ownerKey);
    if (!normalizedKey || normalizedKey === normalizeOwnerKey(DEFAULT_OWNER_FILTER_LABEL)) return;
    setFavoriteOwnerKeys((prev) => {
      const normalizedPrev = prev.map((entry) => normalizeOwnerKey(entry));
      if (normalizedPrev.includes(normalizedKey)) {
        return prev.filter((entry) => normalizeOwnerKey(entry) !== normalizedKey);
      }
      return [...normalizedPrev, normalizedKey];
    });
  };

  const handleSelectClient = (clientId: string, event: React.MouseEvent) => {
    event.stopPropagation();
    setSelectedClients(prev => {
      const newSet = new Set(prev);
      if (newSet.has(clientId)) {
        newSet.delete(clientId);
      } else {
        newSet.add(clientId);
      }
      return newSet;
    });
  };

  const handleSelectAll = () => {
    if (selectedClients.size === filteredClients.length) {
      setSelectedClients(new Set());
    } else {
      setSelectedClients(new Set(filteredClients.map((c: Client) => c.id)));
    }
  };

  const handleClientClick = (client: Client) => {
    setSelectedClient(client);
    setShowSidebar(true);
  };

  useEffect(() => {
    if (!selectedClient) return;

    setNotes(parseClientNotes(selectedClient.notes));
    setCurrentNoteContent('');
    setScheduledActivityType('call');
    setScheduledActivityText(SCHEDULE_ACTIVITY_LABELS.call);
    setShowScheduledActivityTypeDropdown(false);
    setShowActivityCalendar(false);
    setShowCalendarToggleTooltip(false);
    setActivityNowTick(Date.now());
    setActivitySchedulerForm(
      createDefaultDetailedActivityFormData(selectedClient, 'call', SCHEDULE_ACTIVITY_LABELS.call)
    );
    setActivityDealLeadSearch('');
    setActivityPeopleSearch('');
    setActivityOrganizationSearch('');
    setShowActivityDealLeadDropdown(false);
    setShowActivityPeopleDropdown(false);
    setShowActivityOrganizationDropdown(false);
    setShowAllFields(false);
    setShowAllPersonFields(false);
    setShowAllOrganizationFields(false);
    setHoveredField(null);
    setEditingDetailFieldId(null);
    setDetailFieldDraftValue('');
    setDetailFieldDraftChecked(false);
    setShowLabelsDropdownField(null);
    setLabelSearchQuery('');

    if (noteEditorRef.current) {
      noteEditorRef.current.innerHTML = '';
      noteEditorRef.current.setAttribute('data-placeholder', 'Take a note, @name...');
    }
    setIsNoteEditorFocused(false);
  }, [selectedClient?.id]);

  useEffect(() => {
    setSelectedClients(new Set());
    if (!selectedClient) return;

    const shouldCloseSidebar =
      (clientMailboxView === 'inbox' && isClientArchived(selectedClient))
      || (clientMailboxView === 'archive' && !isClientArchived(selectedClient));

    if (shouldCloseSidebar) {
      setShowSidebar(false);
      setSelectedClient(null);
    }
  }, [clientMailboxView, selectedClient]);

  const toDateInputValue = (dateValue?: string): string => {
    if (!dateValue) return '';
    if (/^\d{4}-\d{2}-\d{2}$/.test(dateValue)) return dateValue;
    const usMatch = dateValue.match(/^(\d{1,2})\/(\d{1,2})\/(\d{4})$/);
    if (usMatch) {
      const month = usMatch[1].padStart(2, '0');
      const day = usMatch[2].padStart(2, '0');
      const year = usMatch[3];
      return `${year}-${month}-${day}`;
    }
    const parsed = new Date(dateValue);
    if (!Number.isNaN(parsed.getTime())) {
      return getDateKeyInTimezone(parsed, tenantTimezone) || '';
    }
    return '';
  };

  const clientLabels = Array.isArray(selectedClient?.tags) ? (selectedClient?.tags || []) : [];
  const hasTags = clientLabels.length > 0;

  const startEditingDetailField = (fieldId: ClientEditableFieldId) => {
    if (!selectedClient) return;
    const config = CLIENT_EDITABLE_FIELD_CONFIG[fieldId];
    if (!config) return;

    const currentValue = selectedClient[config.clientKey];
    if (config.inputType === 'checkbox') {
      setDetailFieldDraftChecked(Boolean(currentValue));
      setDetailFieldDraftValue('');
    } else if (config.inputType === 'date') {
      setDetailFieldDraftValue(toDateInputValue(String(currentValue ?? '')));
    } else if (fieldId === 'owner') {
      const explicitOwner = typeof currentValue === 'string' ? currentValue.trim() : '';
      if (explicitOwner) {
        setDetailFieldDraftValue(explicitOwner);
      } else {
        setDetailFieldDraftValue(selectedClient.visibility === 'global' ? DEFAULT_OWNER_FILTER_LABEL : selfOwnerBaseLabel);
      }
    } else if (fieldId === 'visibility') {
      setDetailFieldDraftValue(selectedClient.visibility === 'personal' ? 'personal' : 'global');
    } else if (fieldId === 'businessUnit') {
      const businessUnitValue = typeof currentValue === 'string' ? currentValue.trim() : '';
      setDetailFieldDraftValue(businessUnitValue || (selectedClient.inStore ? 'In-Store' : ''));
    } else {
      setDetailFieldDraftValue(typeof currentValue === 'string' ? currentValue : '');
    }
    setEditingDetailFieldId(fieldId);
  };

  const handleSaveDetailField = async (fieldId: ClientEditableFieldId) => {
    if (!selectedClient) return;
    const config = CLIENT_EDITABLE_FIELD_CONFIG[fieldId];
    if (!config) return;

    if (fieldId === 'businessUnit') {
      const currentBusinessUnit = String(selectedClient.businessUnit || '').trim() || (selectedClient.inStore ? 'In-Store' : '');
      const nextBusinessUnit = detailFieldDraftValue.trim();
      const nextInStore = nextBusinessUnit.toLowerCase() === 'in-store' || nextBusinessUnit.toLowerCase() === 'in store';

      if (currentBusinessUnit === nextBusinessUnit && Boolean(selectedClient.inStore) === nextInStore) {
        setEditingDetailFieldId(null);
        return;
      }

      try {
        await updateClient({
          clientId: selectedClient.id,
          businessUnit: nextBusinessUnit,
          inStore: nextInStore,
        }).unwrap();

        const nextClient: Client = { ...selectedClient, businessUnit: nextBusinessUnit, inStore: nextInStore };
        setSelectedClient(nextClient);
        setEditingDetailFieldId(null);
        setHoveredField(null);
      } catch (error: any) {
        console.error('Error updating business unit:', error);
        alert(`Failed to update Business Unit: ${error?.data?.message || error?.message || 'Unknown error'}`);
      }
      return;
    }

    if (fieldId === 'owner') {
      const currentVisibility = selectedClient.visibility === 'personal' ? 'personal' : 'global';
      const selectedOwner = String(detailFieldDraftValue || '').trim() || DEFAULT_OWNER_FILTER_LABEL;
      const nextVisibility = selectedOwner.toLowerCase() === DEFAULT_OWNER_FILTER_LABEL.toLowerCase() ? 'global' : 'personal';
      if (currentVisibility === nextVisibility) {
        setEditingDetailFieldId(null);
        return;
      }

      try {
        await updateClient({
          clientId: selectedClient.id,
          visibility: nextVisibility,
        } as any).unwrap();

        const nextClient: Client = { ...selectedClient, visibility: nextVisibility };
        setSelectedClient(nextClient);
        setEditingDetailFieldId(null);
        setHoveredField(null);
      } catch (error: any) {
        console.error('Error updating owner visibility mapping:', error);
        alert(`Failed to update Owner: ${error?.data?.message || error?.message || 'Unknown error'}`);
      }
      return;
    }

    if (fieldId === 'visibility') {
      const currentVisibility = selectedClient.visibility === 'personal' ? 'personal' : 'global';
      const nextVisibility = String(detailFieldDraftValue || '').trim().toLowerCase() === 'personal' ? 'personal' : 'global';
      if (currentVisibility === nextVisibility) {
        setEditingDetailFieldId(null);
        return;
      }

      try {
        await updateClient({
          clientId: selectedClient.id,
          visibility: nextVisibility,
        } as any).unwrap();

        const nextClient: Client = { ...selectedClient, visibility: nextVisibility };
        setSelectedClient(nextClient);
        setEditingDetailFieldId(null);
        setHoveredField(null);
      } catch (error: any) {
        console.error('Error updating visibility:', error);
        alert(`Failed to update Visibility: ${error?.data?.message || error?.message || 'Unknown error'}`);
      }
      return;
    }

    const currentValue = selectedClient[config.clientKey];
    let nextValue: string | boolean = '';

    if (config.inputType === 'checkbox') {
      nextValue = detailFieldDraftChecked;
      if (Boolean(currentValue) === nextValue) {
        setEditingDetailFieldId(null);
        return;
      }
    } else if (config.inputType === 'date') {
      nextValue = formatHtmlDateValueToUsStorage(detailFieldDraftValue);
      if (normalizeClientDateToUsStorage(String(currentValue ?? '')) === nextValue) {
        setEditingDetailFieldId(null);
        return;
      }
    } else {
      nextValue = detailFieldDraftValue.trim();
      if (String(currentValue || '') === nextValue) {
        setEditingDetailFieldId(null);
        return;
      }
    }

    try {
      if (fieldId === 'organization') {
        const nextOrgName = String(nextValue || '').trim();
        const existingAccount = nextOrgName ? accountNameMap.get(nextOrgName.toLowerCase()) : null;
        let nextAccountId = '';

        if (nextOrgName) {
          if (existingAccount?.id) {
            nextAccountId = String(existingAccount.id);
          } else {
            const created = await createAccount({ name: nextOrgName }).unwrap();
            const createdAccount = (created?.data?.account || created?.account || null);
            nextAccountId = createdAccount?.id ? String(createdAccount.id) : '';
            refetchAccounts();
          }
        }

        await updateClient({
          clientId: selectedClient.id,
          organization: nextOrgName,
          accountId: nextAccountId || undefined,
        } as any).unwrap();

        const nextClient: Client = {
          ...selectedClient,
          organization: nextOrgName,
          accountId: nextAccountId || undefined,
        };
        setSelectedClient(nextClient);
        setEditingDetailFieldId(null);
        setHoveredField(null);
        return;
      }

      const updatePayload = await updateClient({
        clientId: selectedClient.id,
        [config.clientKey]: nextValue,
      } as any).unwrap();

      const p = updatePayload as { data?: { client?: Client }; client?: Client };
      const serverClient = p?.data?.client ?? p?.client;
      const mergedBase: Client =
        serverClient && serverClient.id === selectedClient.id
          ? { ...selectedClient, ...serverClient }
          : { ...selectedClient };
      // Re-apply the edited field so the sidebar matches the save even if the PUT response omits it.
      const nextClient: Client = { ...mergedBase, [config.clientKey]: nextValue } as Client;
      setSelectedClient(nextClient);
      setEditingDetailFieldId(null);
      setHoveredField(null);
    } catch (error: any) {
      console.error(`Error updating ${String(config.clientKey)}:`, error);
      alert(`Failed to update ${config.placeholder}: ${error?.data?.message || error?.message || 'Unknown error'}`);
    }
  };

  const toggleClientLabel = async (label: string) => {
    if (!selectedClient) return;
    const currentLabels = Array.isArray(selectedClient.tags) ? selectedClient.tags : [];
    const isAssigned = currentLabels.includes(label);
    const updatedLabels = isAssigned
      ? currentLabels.filter((item) => item !== label)
      : [...currentLabels, label];

    try {
      await updateClient({
        clientId: selectedClient.id,
        tags: updatedLabels
      }).unwrap();
      setSelectedClient({ ...selectedClient, tags: updatedLabels });
    } catch (error: any) {
      console.error('Error updating client labels:', error);
      alert(`Failed to update labels: ${error?.data?.message || error?.message || 'Unknown error'}`);
    }
  };

  const handleCreateNewLabel = async () => {
    if (!selectedClient) return;
    const rawValue = window.prompt('Label name');
    if (!rawValue) return;
    const nextLabel = rawValue.trim();
    if (!nextLabel) return;

    const existingLabel = allAvailableLabels.find(
      (label) => label.toLowerCase() === nextLabel.toLowerCase()
    );

    const finalLabel = existingLabel || nextLabel;
    if (!existingLabel) {
      const updatedCustomLabels = [...customLabels, finalLabel];
      setCustomLabels(updatedCustomLabels);
      localStorage.setItem('customLabels', JSON.stringify(updatedCustomLabels));
    }

    await toggleClientLabel(finalLabel);
  };

  const handleCreateFilterLabel = () => {
    const rawValue = window.prompt('Label name');
    if (!rawValue) return;
    const nextLabel = rawValue.trim();
    if (!nextLabel) return;

    const existingLabel = allAvailableLabels.find(
      (label) => label.toLowerCase() === nextLabel.toLowerCase()
    );

    if (!existingLabel) {
      const updatedCustomLabels = [...customLabels, nextLabel];
      setCustomLabels(updatedCustomLabels);
      localStorage.setItem('customLabels', JSON.stringify(updatedCustomLabels));
    }

    setShowTagsFilter(false);
    setLabelsFilterSearchQuery('');
  };

  const renderEditableLabelsRow = (
    fieldId: LabelsFieldId,
    showAll: boolean
  ) => {
    if (!selectedClient) return null;

    const isOpen = showLabelsDropdownField === fieldId;
    const isHovered = hoveredField === fieldId;
    const showAddPrompt = isHovered && !hasTags;
    const shouldRender = showAll || hasTags || isOpen;
    if (!shouldRender) return null;

    const filteredLabelOptions = allAvailableLabels.filter((label) =>
      label.toLowerCase().includes(labelSearchQuery.toLowerCase())
    );

    return (
      <Dropdown
        key={fieldId}
        show={isOpen}
        onToggle={(nextIsOpen) => {
          if (nextIsOpen) {
            setShowLabelsDropdownField(fieldId);
          } else {
            setShowLabelsDropdownField(null);
            setLabelSearchQuery('');
          }
        }}
        className="mb-2"
        style={{ marginBottom: 0 }}
      >
        <Dropdown.Toggle
          as="div"
          className="text-decoration-none labels-dropdown-toggle"
          style={{
            width: '100%',
            padding: '0.5rem',
            borderRadius: '4px',
            backgroundColor: isHovered ? '#f8f9fa' : 'transparent',
            transition: 'background-color 0.2s',
            cursor: 'pointer',
            minHeight: '32px',
            border: 'none',
            textAlign: 'left'
          }}
          onMouseEnter={() => setHoveredField(fieldId)}
          onMouseLeave={() => setHoveredField(null)}
        >
          <div className="d-flex align-items-center gap-2">
            <span style={detailFieldIconStyle}>
              <Tag size={14} />
            </span>
            {hasTags ? (
              <div className="d-flex flex-wrap gap-1 align-items-center">
                {clientLabels.map((label) => (
                  <Badge key={`${fieldId}-${label}`} bg={getLabelVariant(label)} style={{ fontSize: '0.55rem', padding: '0.15rem 0.35rem' }}>
                    {label}
                  </Badge>
                ))}
              </div>
            ) : (
              <span
                style={{
                  fontSize: '0.75rem',
                  color: showAddPrompt ? '#0d6efd' : '#6c757d',
                  fontWeight: showAddPrompt ? 500 : 'normal'
                }}
              >
                {showAddPrompt ? '+ Add Labels' : 'Labels'}
              </span>
            )}
          </div>
        </Dropdown.Toggle>
        <Dropdown.Menu
          show={isOpen}
          style={{
            minWidth: '250px',
            maxHeight: '400px',
            overflowY: 'auto',
            padding: '0.5rem',
            marginTop: '0.25rem'
          }}
        >
          <div className="mb-2">
            <InputGroup size="sm">
              <InputGroup.Text>
                <Search size={14} />
              </InputGroup.Text>
              <Form.Control
                placeholder="Search label"
                value={labelSearchQuery}
                onChange={(e) => setLabelSearchQuery(e.target.value)}
                onClick={(e) => e.stopPropagation()}
                style={{ fontSize: '0.75rem' }}
              />
            </InputGroup>
          </div>

          {filteredLabelOptions.map((label) => {
            const isAssigned = clientLabels.includes(label);
            return (
              <Dropdown.Item
                key={`${fieldId}-${label}-option`}
                active={isAssigned}
                onClick={async (e) => {
                  e.preventDefault();
                  e.stopPropagation();
                  await toggleClientLabel(label);
                }}
                style={{ fontSize: '0.75rem' }}
              >
                <div className="d-flex align-items-center">
                  {isAssigned && <Check className="me-2" size={14} style={{ color: '#0d6efd' }} />}
                  <Badge bg={getLabelVariant(label)} style={{ fontSize: '0.6rem', padding: '0.15rem 0.4rem' }}>
                    {label}
                  </Badge>
                </div>
              </Dropdown.Item>
            );
          })}

          <Dropdown.Divider />
          <Dropdown.Item
            onClick={async (e) => {
              e.preventDefault();
              e.stopPropagation();
              await handleCreateNewLabel();
            }}
            style={{ fontSize: '0.75rem', color: '#0d6efd' }}
          >
            <div className="d-flex align-items-center">
              <Plus className="me-2" size={14} />
              <span>Add new label</span>
            </div>
          </Dropdown.Item>
        </Dropdown.Menu>
      </Dropdown>
    );
  };

  const renderEditableClientRow = (
    fieldId: ClientEditableFieldId,
    label: string,
    Icon: React.ComponentType<{ size?: number; className?: string }>,
    options: { showAll: boolean; showInfo?: boolean }
  ) => {
    if (!selectedClient) return null;
    const config = CLIENT_EDITABLE_FIELD_CONFIG[fieldId];
    if (!config) return null;

    const rawValue = selectedClient[config.clientKey];
    const displayValue = fieldId === 'businessUnit'
      ? (String(rawValue || '').trim() || (selectedClient.inStore ? 'In-Store' : ''))
      : fieldId === 'owner'
        ? (String(rawValue || '').trim() || (selectedClient.visibility === 'global' ? DEFAULT_OWNER_FILTER_LABEL : selfOwnerBaseLabel))
        : fieldId === 'visibility'
          ? (selectedClient.visibility === 'personal' ? 'Personal (private)' : 'Global (visible for everyone)')
          : rawValue;
    const isEditing = editingDetailFieldId === fieldId;
    const isHovered = hoveredField === fieldId;
    const hasValue = config.inputType === 'checkbox'
      ? Boolean(displayValue)
      : String(displayValue || '').trim().length > 0;
    const shouldRender =
      fieldId === 'accountStage'
      || fieldId === 'contactStage'
      || fieldId === 'owner'
      || fieldId === 'visibility'
      || options.showAll
      || hasValue
      || isEditing;
    if (!shouldRender) return null;

    const showAddPrompt = isHovered && !hasValue && !isEditing;
    const detailPrimaryText =
      config.inputType === 'checkbox'
        ? label
        : config.inputType === 'date'
          ? hasValue
            ? (() => {
                const usDate = normalizeClientDateToUsStorage(String(rawValue ?? displayValue ?? ''));
                const title = String(config.placeholder || 'Date').trim();
                return usDate ? `${title}: ${usDate}` : String(displayValue || '').trim();
              })()
            : showAddPrompt
              ? '+ Add value'
              : label
          : hasValue
            ? String(displayValue || '').trim()
            : showAddPrompt
              ? '+ Add value'
              : label;
    const inputType = config.inputType === 'date'
      ? 'date'
      : fieldId.toLowerCase().includes('email')
        ? 'email'
        : fieldId.toLowerCase().includes('phone')
          ? 'tel'
          : 'text';
    const businessUnitOptions = Array.from(new Set(
      ['In-Store', String(selectedClient.businessUnit || '').trim()].filter(Boolean)
    ));
    const stageOptions = fieldId === 'accountStage'
      ? accountStageOptions
      : fieldId === 'contactStage'
        ? contactStageOptions
        : [];

    return (
      <div
        key={fieldId}
        className="mb-2"
        style={{
          cursor: 'pointer',
          padding: '0.5rem',
          borderRadius: '4px',
          backgroundColor: isHovered && !isEditing ? '#f8f9fa' : 'transparent',
          transition: 'background-color 0.2s'
        }}
        onMouseEnter={() => setHoveredField(fieldId)}
        onMouseLeave={() => setHoveredField(null)}
        onClick={() => startEditingDetailField(fieldId)}
      >
        <div className="d-flex align-items-center gap-2">
          <span style={detailFieldIconStyle}>
            <Icon size={14} />
          </span>
          <span
            style={{
              fontSize: '0.75rem',
              color: showAddPrompt ? '#0d6efd' : (hasValue ? '#212529' : '#6c757d'),
              fontWeight: showAddPrompt ? 500 : 'normal'
            }}
          >
            {detailPrimaryText}
          </span>
          {options.showInfo && <InfoCircle size={9} className="ms-1 text-muted" />}
        </div>
        {isEditing && (
          <div className="d-flex align-items-center gap-2 mt-2" onClick={(e) => e.stopPropagation()}>
            {fieldId === 'owner' ? (
              <Form.Select
                value={detailFieldDraftValue || (selectedClient.visibility === 'global' ? DEFAULT_OWNER_FILTER_LABEL : selfOwnerBaseLabel)}
                onChange={(event) => setDetailFieldDraftValue(event.target.value)}
                style={{ fontSize: '0.75rem', padding: '0.25rem 0.5rem', flex: 1 }}
                autoFocus
              >
                {detailOwnerOptions.map((option) => (
                  <option key={`client-owner-option-${option}`} value={option}>
                    {option}
                  </option>
                ))}
              </Form.Select>
            ) : fieldId === 'visibility' ? (
              <Form.Select
                value={detailFieldDraftValue || (selectedClient.visibility === 'personal' ? 'personal' : 'global')}
                onChange={(event) => setDetailFieldDraftValue(event.target.value)}
                style={{ fontSize: '0.75rem', padding: '0.25rem 0.5rem', flex: 1 }}
                autoFocus
              >
                <option value="global">Global (visible for everyone)</option>
                <option value="personal">Personal (private)</option>
              </Form.Select>
            ) : config.inputType === 'checkbox' ? (
              <Form.Check
                type="checkbox"
                checked={detailFieldDraftChecked}
                label={label}
                onChange={(event) => setDetailFieldDraftChecked(event.target.checked)}
                style={{ fontSize: '0.75rem', flex: 1 }}
              />
            ) : (fieldId === 'accountStage' || fieldId === 'contactStage') ? (
              <Form.Select
                value={detailFieldDraftValue}
                onChange={(event) => setDetailFieldDraftValue(event.target.value)}
                style={{ fontSize: '0.75rem', padding: '0.25rem 0.5rem', flex: 1 }}
                autoFocus
              >
                <option value="">Select</option>
                {stageOptions.map((option) => (
                  <option key={`${fieldId}-stage-${option}`} value={option}>
                    {option}
                  </option>
                ))}
              </Form.Select>
            ) : fieldId === 'businessUnit' ? (
              <Form.Select
                value={detailFieldDraftValue}
                onChange={(event) => setDetailFieldDraftValue(event.target.value)}
                style={{ fontSize: '0.75rem', padding: '0.25rem 0.5rem', flex: 1 }}
                autoFocus
              >
                <option value="">Select business unit</option>
                {businessUnitOptions.map((option) => (
                  <option key={option} value={option}>
                    {option}
                  </option>
                ))}
              </Form.Select>
            ) : (
              <Form.Control
                type={inputType}
                value={detailFieldDraftValue}
                placeholder={config.inputType === 'date' ? config.placeholder : label}
                onChange={(event) => setDetailFieldDraftValue(event.target.value)}
                style={{ fontSize: '0.75rem', padding: '0.25rem 0.5rem', flex: 1 }}
                autoFocus
              />
            )}
            {fieldId !== 'owner' && fieldId !== 'visibility' && (
              <Button
                variant="outline-danger"
                size="sm"
                onClick={(event) => {
                  event.stopPropagation();
                  if (config.inputType === 'checkbox') {
                    setDetailFieldDraftChecked(false);
                  } else {
                    setDetailFieldDraftValue('');
                  }
                }}
                style={{ fontSize: '0.75rem', padding: '0.25rem 0.5rem', whiteSpace: 'nowrap' }}
              >
                Clear
              </Button>
            )}
            <Button
              variant="outline-secondary"
              size="sm"
              onClick={(event) => {
                event.stopPropagation();
                setEditingDetailFieldId(null);
              }}
              style={{ fontSize: '0.75rem', padding: '0.25rem 0.5rem', whiteSpace: 'nowrap' }}
            >
              Cancel
            </Button>
            <Button
              variant="primary"
              size="sm"
              onClick={(event) => {
                event.stopPropagation();
                handleSaveDetailField(fieldId);
              }}
              style={{ fontSize: '0.75rem', padding: '0.25rem 0.5rem', whiteSpace: 'nowrap' }}
            >
              Save
            </Button>
          </div>
        )}
      </div>
    );
  };

  const upcomingFocusActivities = useMemo(() => {
    if (!selectedClient) return [] as ScheduledMeetingActivity[];

    const now = Date.now();
    return (meetings as ScheduledMeetingActivity[])
      .filter((meeting) => String(meeting.clientId || '') === selectedClient.id)
      .filter((meeting) => typeof meeting.startTime === 'string' && meeting.startTime.length > 0)
      .filter((meeting) => {
        const startMs = new Date(meeting.startTime).getTime();
        return !Number.isNaN(startMs) && startMs >= (now - (5 * 60 * 1000));
      })
      .sort((a, b) => new Date(a.startTime).getTime() - new Date(b.startTime).getTime());
  }, [meetings, selectedClient]);

  const clientActivityTimeline = useMemo(() => {
    if (!selectedClient) return [] as ScheduledMeetingActivity[];

    return (meetings as ScheduledMeetingActivity[])
      .filter((meeting) => String(meeting.clientId || '') === selectedClient.id)
      .filter((meeting) => typeof meeting.startTime === 'string' && meeting.startTime.length > 0)
      .sort((a, b) => new Date(b.startTime).getTime() - new Date(a.startTime).getTime());
  }, [meetings, selectedClient]);

  const clientEmailThreads = useMemo(
    () => buildEmailThreadsForEntity(clientEmailThreadsData, clientEmailAddress),
    [clientEmailThreadsData, clientEmailAddress]
  );

  const pinnedFocusNotes = useMemo(() => {
    return [...notes]
      .filter((note) => note.pinned)
      .sort((a, b) => {
        const aPinnedAt = new Date(a.pinnedAt || a.timestamp).getTime();
        const bPinnedAt = new Date(b.pinnedAt || b.timestamp).getTime();
        return bPinnedAt - aPinnedAt;
      });
  }, [notes]);

  const splitClientName = (rawName: string): { firstName: string; lastName: string } => {
    const normalized = String(rawName || '').trim().replace(/\s+/g, ' ');
    if (!normalized) return { firstName: 'Converted', lastName: 'Client' };
    const parts = normalized.split(' ');
    if (parts.length === 1) return { firstName: parts[0], lastName: 'Client' };
    return {
      firstName: parts[0],
      lastName: parts.slice(1).join(' ') || 'Client',
    };
  };

  const handleConvertSelectedClient = async (target: 'Contact' | 'Lead' | 'Deal') => {
    if (!selectedClient || isConvertingClient) return;

    const clientId = selectedClient.id;
    const visibility = selectedClient.visibility === 'personal' ? 'personal' : 'global';
    const fallbackName = String(selectedClient.name || '').trim()
      || String(selectedClient.organization || '').trim()
      || String(selectedClient.email || '').trim()
      || 'Converted Client';
    const parsedNotes = parseClientNotes(selectedClient.notes);
    const rawStringNote = typeof selectedClient.notes === 'string' ? selectedClient.notes.trim() : '';
    const fallbackTimestamp = String(selectedClient.updatedAt || selectedClient.createdAt || new Date().toISOString());
    const structuredNotes = parsedNotes.length > 0
      ? parsedNotes
      : (rawStringNote
        ? [{
            id: `note-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`,
            content: rawStringNote,
            timestamp: fallbackTimestamp,
            author: 'PhiLL Kross',
            pinned: false,
          }]
        : []);
    const notesText = [...structuredNotes.map((note) => String(note.content || '').trim())]
      .filter(Boolean)
      .join('\n\n');

    setIsConvertingClient(true);
    try {
      if (target === 'Contact') {
        const { firstName, lastName } = splitClientName(selectedClient.name || fallbackName);
        await createContact({
          firstName,
          lastName,
          organization: selectedClient.organization || undefined,
          email: selectedClient.email || undefined,
          phone: selectedClient.phone || undefined,
          address: selectedClient.address || undefined,
          city: selectedClient.city || undefined,
          state: selectedClient.state || undefined,
          zipCode: selectedClient.zipCode || undefined,
          country: selectedClient.country || undefined,
          notes: structuredNotes,
          accountStage: selectedClient.accountStage || undefined,
          contactStage: selectedClient.contactStage || undefined,
          value: selectedClient.value || undefined,
          expectedCloseDate: selectedClient.expectedCloseDate || undefined,
          businessUnit: selectedClient.businessUnit || undefined,
          inStore: Boolean(selectedClient.inStore),
          sourceOrigin: selectedClient.sourceOrigin || undefined,
          sourceChannel: selectedClient.sourceChannel || undefined,
          sourceChannelId: selectedClient.sourceChannelId || undefined,
          linkedInProfile: selectedClient.linkedInProfile || undefined,
          numberOfEmployees: selectedClient.numberOfEmployees || undefined,
          annualRevenue: selectedClient.annualRevenue || undefined,
          visibility,
        }).unwrap();
      } else if (target === 'Lead') {
        await createLead({
          name: fallbackName,
          organization: selectedClient.organization || undefined,
          accountStage: selectedClient.accountStage || undefined,
          contactStage: selectedClient.contactStage || undefined,
          value: selectedClient.value || undefined,
          expectedCloseDate: selectedClient.expectedCloseDate || undefined,
          sourceOrigin: selectedClient.sourceOrigin || undefined,
          sourceChannel: selectedClient.sourceChannel || undefined,
          sourceChannelId: selectedClient.sourceChannelId || undefined,
          businessUnit: selectedClient.businessUnit || undefined,
          inStore: Boolean(selectedClient.inStore),
          country: selectedClient.country || undefined,
          email: selectedClient.email || undefined,
          phone: selectedClient.phone || undefined,
          address: selectedClient.address || undefined,
          website: selectedClient.website || undefined,
          linkedInProfile: selectedClient.linkedInProfile || undefined,
          numberOfEmployees: selectedClient.numberOfEmployees || undefined,
          annualRevenue: selectedClient.annualRevenue || undefined,
          industry: selectedClient.industry || undefined,
          notes: structuredNotes,
          status: 'Lead',
          visibility,
        }).unwrap();
      } else {
        const dealName = String(selectedClient.name || '').trim()
          ? `${selectedClient.name}${selectedClient.organization ? ` - ${selectedClient.organization}` : ''}`
          : (selectedClient.organization || 'Converted Client Deal');
        await createDeal({
          name: dealName,
          value: selectedClient.value || '0',
          expectedCloseDate: selectedClient.expectedCloseDate || undefined,
          clientId: selectedClient.id,
          notes: notesText || undefined,
          visibility,
        }).unwrap();
      }

      await deleteClient(clientId).unwrap();
      setSelectedClients((prev) => {
        const next = new Set(prev);
        next.delete(clientId);
        return next;
      });
      setShowSidebar(false);
      setSelectedClient(null);
      refetchClients();
      alert(`Converted client to ${target.toLowerCase()}.`);
    } catch (error: any) {
      alert(`Failed to convert client to ${target.toLowerCase()}: ${error?.data?.message || error?.message || 'Unknown error'}`);
    } finally {
      setIsConvertingClient(false);
    }
  };

  const handleSaveClient = async () => {
    try {
      const normalizedFirstName = String(formData.firstName || '').trim();
      const normalizedLastName = String(formData.lastName || '').trim();
      if (!normalizedFirstName || !normalizedLastName) {
        alert('First Name and Last Name are required.');
        return;
      }
      const combinedName = `${normalizedFirstName} ${normalizedLastName}`.trim();

      const clientData = {
        name: combinedName,
        firstName: normalizedFirstName,
        lastName: normalizedLastName,
        organization: formData.organization || undefined,
        accountStage: formData.accountStage || undefined,
        contactStage: formData.contactStage || undefined,
        industry: formData.industry || undefined,
        website: formData.website || undefined,
        address: formData.address || undefined,
        city: formData.city || undefined,
        state: formData.state || undefined,
        zipCode: formData.zipCode || undefined,
        country: formData.country || undefined,
        phone: formData.phone || undefined,
        email: formData.email || undefined,
        notes: formData.notes || undefined,
        tags: formData.tags ? [formData.tags] : [],
        status: formData.status,
        pastClientReason: formData.status === 'past' ? formData.pastClientReason : undefined,
        visibility: formData.visibility === 'personal' ? 'personal' : 'global',
        decisionMaker: Boolean(formData.decisionMaker)
      };

      await createClient(clientData).unwrap();
      
      setFormData({
        firstName: '',
        lastName: '',
        organization: '',
        accountStage: '',
        contactStage: '',
        visibility: 'global',
        industry: '',
        website: '',
        address: '',
        city: '',
        state: '',
        zipCode: '',
        country: '',
        phone: '',
        email: '',
        notes: '',
        tags: '',
        status: 'current',
        pastClientReason: '',
        decisionMaker: false
      });

      setShowAddClientModal(false);
      refetchClients();
    } catch (error: any) {
      console.error('Error creating client:', error);
      alert(`Failed to create client: ${error?.data?.message || error?.message || 'Unknown error'}`);
    }
  };

  const handleFormChange = (field: string, value: string | boolean) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    if (Number.isNaN(date.getTime())) return '';
    const relativeDayDiff = getRelativeDayDifferenceInTimezone(date, new Date(), tenantTimezone);
    if (relativeDayDiff === 0) {
      return `Today at ${date.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit', ...timezoneOption })}`;
    } else if (relativeDayDiff === -1) {
      return `Yesterday at ${date.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit', ...timezoneOption })}`;
    }
    return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric', ...timezoneOption }) +
      ' at ' + date.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit', ...timezoneOption });
  };

  const getLabelVariant = (label: string): string => {
    const currentColors = getSharedLabelColors();
    return currentColors[label] || currentColors[label.toUpperCase()] || 'secondary';
  };

  const executeCommand = (command: string, value?: string) => {
    document.execCommand(command, false, value);
    if (noteEditorRef.current) noteEditorRef.current.focus();
  };

  const renderScheduledActivityIcon = (activityType: ScheduleActivityType) => {
    if (activityType === 'call' || activityType === 'text') return <Phone size={12} />;
    if (activityType === 'email') return <Envelope size={12} />;
    if (activityType === 'meeting' || activityType === 'deadline') return <Calendar size={12} />;
    if (activityType === 'task') return <CheckSquare size={12} />;
    return <Briefcase size={12} />;
  };

  const getScheduledActivityTypeFromMeeting = (meeting: ScheduledMeetingActivity): ScheduleActivityType => {
    const tags = Array.isArray(meeting.tags) ? meeting.tags : [];
    const activityTag = tags.find((tag) => typeof tag === 'string' && tag.startsWith('activity:'));
    if (activityTag) {
      const parsed = activityTag.replace('activity:', '').trim() as ScheduleActivityType;
      if (SCHEDULE_ACTIVITY_LABELS[parsed]) {
        return parsed;
      }
    }

    if (meeting.meetingType === 'phone') return 'call';
    return 'meeting';
  };

  const formatFocusDateLabel = (dateString: string): string => {
    const date = new Date(dateString);
    if (Number.isNaN(date.getTime())) return '';

    return date.toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      ...timezoneOption
    }) + ' at ' + date.toLocaleTimeString('en-US', {
      hour: 'numeric',
      minute: '2-digit',
      ...timezoneOption
    });
  };

  const getDetailedActivityDefaultStartEnd = () => {
    const now = new Date();
    const end = new Date(now.getTime() + 60 * 60 * 1000);
    const pad = (n: number) => String(n).padStart(2, '0');
    const toLocal = (d: Date) =>
      `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
    return { start: toLocal(now), end: toLocal(end) };
  };

  const createDefaultDetailedActivityFormData = (
    client: Client | null,
    activityType: ScheduleActivityType,
    titleOverride?: string
  ): DetailedActivityFormData => {
    const { start, end } = getDetailedActivityDefaultStartEnd();
    const title = String(titleOverride || '').trim() || SCHEDULE_ACTIVITY_LABELS[activityType];
    return {
      title,
      description: '',
      startTime: start,
      endTime: end,
      location: String(client?.organization || client?.name || '').trim(),
      meetingType: (activityType === 'call' || activityType === 'text') ? 'phone' : 'in-person',
      meetingUrl: '',
      clientId: String(client?.id || ''),
      contactId: '',
      dealId: '',
      leadId: '',
      purposeTypeId: meetingPurposeOptions[0]?.id || DEFAULT_ACTIVITY_PURPOSE_ID,
      notes: '',
      priority: 'normal',
      freeBusy: 'free',
      markAsDone: false,
      videoCallProvider: '',
    };
  };

  const handleActivitySchedulerFieldChange = (field: keyof DetailedActivityFormData, value: string | boolean) => {
    setActivitySchedulerForm((prev) => ({
      ...prev,
      [field]: value,
    }));
  };

  const handleSelectDetailedActivityType = (activityType: ScheduleActivityType) => {
    const nextLabel = SCHEDULE_ACTIVITY_LABELS[activityType];
    setScheduledActivityType(activityType);
    setScheduledActivityText(nextLabel);
    setActivitySchedulerForm((prev) => ({
      ...prev,
      title: nextLabel,
      meetingType:
        activityType === 'call' || activityType === 'text'
          ? 'phone'
          : (prev.videoCallProvider ? 'video' : 'in-person'),
    }));
  };

  const getActivitySchedulerDisplayDate = () => {
    if (activitySchedulerForm.startTime) {
      const parsed = new Date(activitySchedulerForm.startTime);
      if (!Number.isNaN(parsed.getTime())) return parsed;
    }
    return new Date();
  };

  const shiftActivitySchedulerDay = (delta: number) => {
    const nextDate = getActivitySchedulerDisplayDate();
    nextDate.setDate(nextDate.getDate() + delta);
    const pad = (n: number) => String(n).padStart(2, '0');
    const datePrefix = `${nextDate.getFullYear()}-${pad(nextDate.getMonth() + 1)}-${pad(nextDate.getDate())}T`;
    const fallback = getDetailedActivityDefaultStartEnd();
    const startTime = activitySchedulerForm.startTime || fallback.start;
    const endTime = activitySchedulerForm.endTime || fallback.end;
    const startClock = startTime.slice(11, 16);
    const endClock = endTime.slice(11, 16);
    setActivitySchedulerForm((prev) => ({
      ...prev,
      startTime: `${datePrefix}${startClock}`,
      endTime: `${datePrefix}${endClock}`,
    }));
  };

  const handleActivitySchedulerPrevDay = () => shiftActivitySchedulerDay(-1);
  const handleActivitySchedulerNextDay = () => shiftActivitySchedulerDay(1);
  const handleActivitySchedulerToday = () => {
    const now = new Date();
    const pad = (n: number) => String(n).padStart(2, '0');
    const datePrefix = `${now.getFullYear()}-${pad(now.getMonth() + 1)}-${pad(now.getDate())}T`;
    const fallback = getDetailedActivityDefaultStartEnd();
    const startTime = activitySchedulerForm.startTime || fallback.start;
    const endTime = activitySchedulerForm.endTime || fallback.end;
    const startClock = startTime.slice(11, 16);
    const endClock = endTime.slice(11, 16);
    setActivitySchedulerForm((prev) => ({
      ...prev,
      startTime: `${datePrefix}${startClock}`,
      endTime: `${datePrefix}${endClock}`,
    }));
  };

  const activityCalendarStartHour = useMemo(() => {
    const displayDate = getActivitySchedulerDisplayDate();
    const now = new Date(activityNowTick);
    const isDisplayToday =
      displayDate.getDate() === now.getDate()
      && displayDate.getMonth() === now.getMonth()
      && displayDate.getFullYear() === now.getFullYear();
    const selectedStart = new Date(activitySchedulerForm.startTime);
    const hasSelectedStart = !Number.isNaN(selectedStart.getTime());
    const sameDayAsDisplay =
      hasSelectedStart
      && selectedStart.getDate() === displayDate.getDate()
      && selectedStart.getMonth() === displayDate.getMonth()
      && selectedStart.getFullYear() === displayDate.getFullYear();

    const selectedHour = sameDayAsDisplay ? selectedStart.getHours() : displayDate.getHours();
    const nowHour = now.getHours();
    let proposedStart = selectedHour - 4;

    if (isDisplayToday && sameDayAsDisplay && Math.abs(selectedHour - nowHour) < ACTIVITY_CALENDAR_VISIBLE_HOURS) {
      proposedStart = Math.min(selectedHour, nowHour) - 1;
    }

    if (isDisplayToday) {
      const minStartForNow = nowHour - ACTIVITY_CALENDAR_VISIBLE_HOURS + 1;
      const maxStartForNow = nowHour;
      proposedStart = Math.max(proposedStart, minStartForNow);
      proposedStart = Math.min(proposedStart, maxStartForNow);
    }

    return Math.max(0, Math.min(24 - ACTIVITY_CALENDAR_VISIBLE_HOURS, proposedStart));
  }, [activitySchedulerForm.startTime, activityNowTick]);

  useEffect(() => {
    const intervalId = window.setInterval(() => {
      setActivityNowTick(Date.now());
    }, 60000);
    return () => window.clearInterval(intervalId);
  }, []);

  const handleActivityCalendarGridClick = (event: React.MouseEvent<HTMLDivElement>) => {
    const rect = event.currentTarget.getBoundingClientRect();
    const relativeY = Math.max(0, Math.min(rect.height - 1, event.clientY - rect.top));
    const slotOffset = relativeY / ACTIVITY_CALENDAR_SLOT_HEIGHT;
    const hourOffset = Math.floor(slotOffset);
    const minutes = (slotOffset - hourOffset) >= 0.5 ? 30 : 0;
    const clickedHour = Math.min(23, activityCalendarStartHour + hourOffset);

    const displayDate = getActivitySchedulerDisplayDate();
    const startDate = new Date(displayDate);
    startDate.setHours(clickedHour, minutes, 0, 0);

    const existingStart = new Date(activitySchedulerForm.startTime);
    const existingEnd = new Date(activitySchedulerForm.endTime);
    const fallbackDurationMs = 60 * 60 * 1000;
    const existingDurationMs =
      !Number.isNaN(existingStart.getTime())
      && !Number.isNaN(existingEnd.getTime())
      && existingEnd.getTime() > existingStart.getTime()
        ? (existingEnd.getTime() - existingStart.getTime())
        : fallbackDurationMs;
    const endDate = new Date(startDate.getTime() + existingDurationMs);

    setActivitySchedulerForm((prev) => ({
      ...prev,
      startTime: toLocalDateTimeInputValue(startDate),
      endTime: toLocalDateTimeInputValue(endDate),
    }));
  };

  const handleCancelDetailedActivity = () => {
    if (!selectedClient) return;
    setActivitySchedulerForm(
      createDefaultDetailedActivityFormData(selectedClient, scheduledActivityType, scheduledActivityText)
    );
    setActivityDealLeadSearch('');
    setActivityPeopleSearch('');
    setActivityOrganizationSearch('');
    setShowActivityDealLeadDropdown(false);
    setShowActivityPeopleDropdown(false);
    setShowActivityOrganizationDropdown(false);
  };

  const handleSelectScheduledActivityType = (nextType: ScheduleActivityType) => {
    setScheduledActivityType(nextType);
    setScheduledActivityText(SCHEDULE_ACTIVITY_LABELS[nextType]);
    setShowScheduledActivityTypeDropdown(false);
  };

  const toLocalDateTimeInputValue = (date: Date): string => {
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    const hours = String(date.getHours()).padStart(2, '0');
    const minutes = String(date.getMinutes()).padStart(2, '0');
    return `${year}-${month}-${day}T${hours}:${minutes}`;
  };

  const getQuickScheduleStart = (preset: QuickSchedulePreset): Date | null => {
    const now = new Date();
    if (preset === 'in1h') return new Date(now.getTime() + (60 * 60 * 1000));
    if (preset === 'in3h') return new Date(now.getTime() + (3 * 60 * 60 * 1000));
    if (preset === 'tomorrow') {
      const tomorrow = new Date(now);
      tomorrow.setDate(tomorrow.getDate() + 1);
      tomorrow.setHours(9, 0, 0, 0);
      return tomorrow;
    }
    if (preset === 'nextWeek') {
      const nextWeek = new Date(now);
      nextWeek.setDate(nextWeek.getDate() + 7);
      nextWeek.setHours(9, 0, 0, 0);
      return nextWeek;
    }

    const proposedDate = new Date(now.getTime() + (60 * 60 * 1000));
    const userInput = window.prompt(
      'Schedule date and time (YYYY-MM-DDTHH:mm)',
      toLocalDateTimeInputValue(proposedDate)
    );
    if (!userInput) return null;

    const parsed = new Date(userInput);
    if (Number.isNaN(parsed.getTime())) {
      alert('Invalid date/time format. Please use YYYY-MM-DDTHH:mm');
      return null;
    }
    return parsed;
  };

  const handleQuickScheduleActivity = async (preset: QuickSchedulePreset) => {
    if (!selectedClient) return;

    const startDate = getQuickScheduleStart(preset);
    if (!startDate) return;

    const title = scheduledActivityText.trim() || SCHEDULE_ACTIVITY_LABELS[scheduledActivityType];
    const endDate = new Date(startDate.getTime() + (30 * 60 * 1000));
    const isPhoneActivity = scheduledActivityType === 'call' || scheduledActivityType === 'text';

    try {
      await createMeeting({
        title,
        startTime: startDate.toISOString(),
        endTime: endDate.toISOString(),
        meetingType: isPhoneActivity ? 'phone' : 'in-person',
        clientId: selectedClient.id,
        location: selectedClient.organization || undefined,
        tags: [`activity:${scheduledActivityType}`],
        visibility: 'personal',
      }).unwrap();

      refetchMeetings();
      refetchClients();
    } catch (error: any) {
      console.error('Error scheduling activity:', error);
      alert(`Failed to schedule activity: ${error?.data?.message || error?.message || 'Unknown error'}`);
    }
  };

  const handleSaveDetailedActivity = async () => {
    if (!selectedClient) return;

    const currentClient = selectedClient;
    const title = String(activitySchedulerForm.title || '').trim() || SCHEDULE_ACTIVITY_LABELS[scheduledActivityType];
    const startDate = new Date(activitySchedulerForm.startTime);
    const endDate = new Date(activitySchedulerForm.endTime);
    if (Number.isNaN(startDate.getTime()) || Number.isNaN(endDate.getTime())) {
      alert('Please set a valid activity date and time.');
      return;
    }

    const purposeTag = `purpose:${activitySchedulerForm.purposeTypeId || (meetingPurposeOptions[0]?.id || DEFAULT_ACTIVITY_PURPOSE_ID)}`;
    const tags = Array.from(new Set([`activity:${scheduledActivityType}`, purposeTag]));

    try {
      await createMeeting({
        title,
        description: activitySchedulerForm.description || undefined,
        startTime: startDate.toISOString(),
        endTime: endDate.toISOString(),
        location: activitySchedulerForm.location || undefined,
        meetingType: activitySchedulerForm.videoCallProvider ? 'video' : activitySchedulerForm.meetingType,
        meetingUrl: activitySchedulerForm.meetingUrl || undefined,
        clientId: activitySchedulerForm.clientId || currentClient.id,
        contactId: activitySchedulerForm.contactId || undefined,
        dealId: activitySchedulerForm.dealId || undefined,
        leadId: activitySchedulerForm.leadId || undefined,
        purposeTypeId: activitySchedulerForm.purposeTypeId || (meetingPurposeOptions[0]?.id || DEFAULT_ACTIVITY_PURPOSE_ID),
        notes: activitySchedulerForm.notes || undefined,
        tags,
        visibility: 'personal',
      }).unwrap();

      refetchMeetings();
      refetchClients();
      setActivitySchedulerForm(
        createDefaultDetailedActivityFormData(currentClient, scheduledActivityType, SCHEDULE_ACTIVITY_LABELS[scheduledActivityType])
      );
      setActivityDealLeadSearch('');
      setActivityPeopleSearch('');
      setActivityOrganizationSearch('');
      setShowActivityDealLeadDropdown(false);
      setShowActivityPeopleDropdown(false);
      setShowActivityOrganizationDropdown(false);
    } catch (error: any) {
      console.error('Error creating detailed activity:', error);
      alert(`Failed to schedule activity: ${error?.data?.message || error?.message || 'Unknown error'}`);
    }
  };

  const handleSaveNote = async () => {
    if (!selectedClient) return;
    const content = noteEditorRef.current?.innerHTML || currentNoteContent;
    const textContent = noteEditorRef.current?.textContent || currentNoteContent.replace(/<[^>]*>/g, '');
    if (!textContent.trim()) return;

    const newNote: ClientNote = {
      id: Date.now().toString(),
      content,
      timestamp: new Date().toISOString(),
      author: 'PhiLL Kross',
      pinned: false
    };
    const nextNotes = [newNote, ...notes];

    try {
      await updateClient({
        clientId: selectedClient.id,
        notes: nextNotes
      }).unwrap();

      setNotes(nextNotes);
      setSelectedClient((prev) => (prev ? { ...prev, notes: nextNotes } : prev));
      setCurrentNoteContent('');
      if (noteEditorRef.current) {
        noteEditorRef.current.innerHTML = '';
        noteEditorRef.current.setAttribute('data-placeholder', 'Take a note, @name...');
      }
      setIsNoteEditorFocused(false);
    } catch (error: any) {
      console.error('Error saving note:', error);
      alert(`Failed to save note: ${error?.data?.message || error?.message || 'Unknown error'}`);
    }
  };

  const handleTogglePinNote = async (noteId: string) => {
    if (!selectedClient) return;

    const nowIso = new Date().toISOString();
    const nextNotes = notes.map((note) => {
      if (note.id !== noteId) return note;
      const nextPinned = !note.pinned;
      return {
        ...note,
        pinned: nextPinned,
        pinnedAt: nextPinned ? nowIso : undefined
      };
    });

    try {
      await updateClient({
        clientId: selectedClient.id,
        notes: nextNotes
      }).unwrap();
      setNotes(nextNotes);
      setSelectedClient((prev) => (prev ? { ...prev, notes: nextNotes } : prev));
    } catch (error: any) {
      console.error('Error toggling note pin:', error);
      alert(`Failed to update note: ${error?.data?.message || error?.message || 'Unknown error'}`);
    }
  };

  const handleDeleteNote = async (noteId: string) => {
    if (!selectedClient) return;
    const shouldDelete = window.confirm('Delete this note?');
    if (!shouldDelete) return;

    const nextNotes = notes.filter((note) => note.id !== noteId);
    try {
      await updateClient({
        clientId: selectedClient.id,
        notes: nextNotes
      }).unwrap();
      setNotes(nextNotes);
      setSelectedClient((prev) => (prev ? { ...prev, notes: nextNotes } : prev));
    } catch (error: any) {
      console.error('Error deleting note:', error);
      alert(`Failed to delete note: ${error?.data?.message || error?.message || 'Unknown error'}`);
    }
  };

  const handleArchiveSelectedClient = async () => {
    if (!selectedClient) return;

    const currentlyArchived = isClientArchived(selectedClient);
    const nextMailboxStatus: Client['mailboxStatus'] = currentlyArchived ? 'inbox' : 'archived';
    const confirmationMessage = currentlyArchived
      ? `Move "${selectedClient.name}" back to Inbox?`
      : `Archive "${selectedClient.name}"?`;

    const shouldProceed = window.confirm(confirmationMessage);
    if (!shouldProceed) return;

    try {
      await updateClient({
        clientId: selectedClient.id,
        mailboxStatus: nextMailboxStatus
      }).unwrap();

      const nextClient: Client = { ...selectedClient, mailboxStatus: nextMailboxStatus };
      setSelectedClient(nextClient);
      if (
        (clientMailboxView === 'inbox' && nextMailboxStatus === 'archived')
        || (clientMailboxView === 'archive' && nextMailboxStatus !== 'archived')
      ) {
        setShowSidebar(false);
        setSelectedClient(null);
      }
    } catch (error: any) {
      console.error('Error updating client mailbox status:', error);
      alert(`Failed to update client status: ${error?.data?.message || error?.message || 'Unknown error'}`);
    }
  };

  const handleDeleteSelectedFromList = async () => {
    const ids = Array.from(selectedClients);
    if (ids.length === 0) return;
    const count = ids.length;
    const shouldDelete = window.confirm(
      count === 1 ? 'Delete the selected client? This cannot be undone.' : `Delete ${count} selected clients? This cannot be undone.`
    );
    if (!shouldDelete) return;

    try {
      for (const id of ids) {
        await deleteClient(id).unwrap();
      }
      setSelectedClients(new Set());
      if (selectedClient && ids.includes(selectedClient.id)) {
        setShowSidebar(false);
        setSelectedClient(null);
      }
      refetchClients();
      alert(count === 1 ? 'Client deleted.' : `${count} clients deleted.`);
    } catch (error: any) {
      alert(`Failed to delete: ${error?.data?.message || error?.message || 'Unknown error'}`);
    }
  };

  const handleDeleteSelectedClient = async () => {
    if (!selectedClient) return;

    const shouldDelete = window.confirm(`Delete client "${selectedClient.name}"? This cannot be undone.`);
    if (!shouldDelete) return;

    try {
      await deleteClient(selectedClient.id).unwrap();
      setSelectedClients((prev) => {
        const next = new Set(prev);
        next.delete(selectedClient.id);
        return next;
      });
      setShowSidebar(false);
      setSelectedClient(null);
      refetchClients();
    } catch (error: any) {
      console.error('Error deleting client:', error);
      alert(`Failed to delete client: ${error?.data?.message || error?.message || 'Unknown error'}`);
    }
  };

  const handleCancelNote = () => {
    setCurrentNoteContent('');
    if (noteEditorRef.current) {
      noteEditorRef.current.innerHTML = '';
      noteEditorRef.current.setAttribute('data-placeholder', 'Take a note, @name...');
    }
    setIsNoteEditorFocused(false);
  };

  if (isLoadingClients) {
    return (
      <Container fluid className="py-4 d-flex justify-content-center align-items-center" style={{ minHeight: '400px' }}>
        <Spinner animation="border" />
      </Container>
    );
  }

  if (clientsError) {
    return (
      <Container fluid className="py-4">
        <Alert variant="danger">
          <Alert.Heading>Error loading clients</Alert.Heading>
          <p>{(clientsError as any)?.data?.message || 'Failed to load clients'}</p>
          <Button onClick={() => refetchClients()}>Retry</Button>
        </Alert>
      </Container>
    );
  }

  return (
    <Container fluid className="p-0" style={{ backgroundColor: '#ffffff', minHeight: '100vh' }}>
      {/* Page Header - same as Leads: title + line under */}
      <div style={{ backgroundColor: '#ffffff', paddingTop: '1rem', paddingBottom: '1rem', borderBottom: '1px solid #dee2e6' }}>
        <Container fluid>
          <div className="d-flex align-items-center justify-content-between">
            <div className="d-flex align-items-center">
              <h5 className="mb-0" style={{ fontSize: '1.25rem', fontWeight: 500, color: '#212529' }}>Clients</h5>
            </div>
            <div className="d-flex align-items-center gap-2">
              <InputGroup size="sm" style={{ width: '200px' }}>
                <InputGroup.Text style={{ backgroundColor: 'white', borderRight: 'none', fontSize: '0.75rem' }}>
                  <Search size={14} />
                </InputGroup.Text>
                <Form.Control
                  type="text"
                  placeholder="Search Kross Global"
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  style={{ borderLeft: 'none', fontSize: '0.75rem' }}
                  size="sm"
                />
              </InputGroup>
            </div>
          </div>
        </Container>
      </div>

      {/* Main Content */}
      <div className="py-3" style={{ fontSize: '0.75rem' }}>
        {/* Action Bar - match Leads font sizes and icon sizes */}
        <div className="d-flex justify-content-between align-items-center mb-3">
          <div className="d-flex align-items-center gap-2">
            <div className="d-flex align-items-center">
              <OverlayTrigger placement="bottom" overlay={<Tooltip id="clients-inbox-view-tooltip">Inbox</Tooltip>}>
                <Button
                  variant={clientMailboxView === 'inbox' ? 'outline-primary' : 'outline-secondary'}
                  size="sm"
                  onClick={() => setClientMailboxView('inbox')}
                  style={{
                    width: '40px',
                    height: '34px',
                    padding: 0,
                    borderTopRightRadius: 0,
                    borderBottomRightRadius: 0,
                    display: 'inline-flex',
                    alignItems: 'center',
                    justifyContent: 'center'
                  }}
                >
                  <Inbox size={14} />
                </Button>
              </OverlayTrigger>
              <OverlayTrigger placement="bottom" overlay={<Tooltip id="clients-archive-view-tooltip">Archive</Tooltip>}>
                <Button
                  variant={clientMailboxView === 'archive' ? 'outline-primary' : 'outline-secondary'}
                  size="sm"
                  onClick={() => setClientMailboxView('archive')}
                  style={{
                    width: '40px',
                    height: '34px',
                    padding: 0,
                    borderTopLeftRadius: 0,
                    borderBottomLeftRadius: 0,
                    marginLeft: '-1px',
                    display: 'inline-flex',
                    alignItems: 'center',
                    justifyContent: 'center'
                  }}
                >
                  <Archive size={14} />
                </Button>
              </OverlayTrigger>
            </div>
            {selectedClients.size > 0 && (
              <OverlayTrigger placement="bottom" overlay={<Tooltip id="clients-delete-selected-tooltip">Delete selected</Tooltip>}>
                <span className="d-inline-block">
                  <Button
                    variant="outline-secondary"
                    size="sm"
                    className="d-flex align-items-center"
                    style={{ fontSize: '0.75rem', padding: '0.25rem 0.5rem' }}
                    disabled={isDeletingClient}
                    onClick={handleDeleteSelectedFromList}
                  >
                    <Trash size={14} />
                  </Button>
                </span>
              </OverlayTrigger>
            )}
            <Button
              variant="primary"
              size="sm"
              className="d-flex align-items-center"
              style={{ fontSize: '0.75rem', padding: '0.25rem 0.5rem' }}
              onClick={() => setShowAddClientModal(true)}
            >
              <Plus className="me-1" size={14} />
              Client
              <ChevronDown className="ms-1" size={12} />
            </Button>
            <span className="text-muted" style={{ fontSize: '0.75rem' }}>{filteredClients.length} clients</span>
          </div>
          <div className="d-flex align-items-center gap-2">
            <Dropdown>
              <Dropdown.Toggle
                variant={selectedStatus ? 'primary' : 'outline-secondary'}
                size="sm"
                id="status-dropdown"
                style={{ fontSize: '0.75rem', padding: '0.25rem 0.5rem' }}
              >
                {selectedStatus === 'current' ? 'Current' : selectedStatus === 'past' ? 'Past' : 'All Status'}
                <ChevronDown size={12} className="ms-1" />
              </Dropdown.Toggle>
              <Dropdown.Menu>
                <Dropdown.Item onClick={() => setSelectedStatus('')}>All Status</Dropdown.Item>
                <Dropdown.Item onClick={() => setSelectedStatus('current')}>Current</Dropdown.Item>
                <Dropdown.Item onClick={() => setSelectedStatus('past')}>Past</Dropdown.Item>
              </Dropdown.Menu>
            </Dropdown>
            <Dropdown>
              <Dropdown.Toggle
                variant={contractRenewalFilter !== 'all' ? 'primary' : 'outline-secondary'}
                size="sm"
                id="clients-contract-renewal-dropdown"
                style={{ fontSize: '0.75rem', padding: '0.25rem 0.5rem' }}
              >
                {contractRenewalFilter === 'overdue'
                  ? 'Renewal: Overdue'
                  : contractRenewalFilter === 'next30'
                    ? 'Renewal: Next 30'
                    : contractRenewalFilter === 'next60'
                      ? 'Renewal: Next 60'
                      : contractRenewalFilter === 'next90'
                        ? 'Renewal: Next 90'
                        : 'Renewal: All'}
                <ChevronDown size={12} className="ms-1" />
              </Dropdown.Toggle>
              <Dropdown.Menu>
                <Dropdown.Item onClick={() => setContractRenewalFilter('all')}>All</Dropdown.Item>
                <Dropdown.Item onClick={() => setContractRenewalFilter('overdue')}>Overdue / expired</Dropdown.Item>
                <Dropdown.Item onClick={() => setContractRenewalFilter('next30')}>Next 30 days</Dropdown.Item>
                <Dropdown.Item onClick={() => setContractRenewalFilter('next60')}>Next 60 days</Dropdown.Item>
                <Dropdown.Item onClick={() => setContractRenewalFilter('next90')}>Next 90 days</Dropdown.Item>
              </Dropdown.Menu>
            </Dropdown>
            <Dropdown
              show={showTagsFilter}
              onToggle={(nextIsOpen) => {
                setShowTagsFilter(nextIsOpen);
                if (!nextIsOpen) setLabelsFilterSearchQuery('');
              }}
            >
              <Dropdown.Toggle
                variant="outline-secondary"
                size="sm"
                style={{ fontSize: '0.75rem', padding: '0.25rem 0.5rem' }}
              >
                {selectedTag || 'All labels'}
                <ChevronDown size={12} className="ms-1" />
              </Dropdown.Toggle>
              <Dropdown.Menu style={{ minWidth: '250px', maxHeight: '400px', overflowY: 'auto', padding: '0.5rem' }}>
                <div className="mb-2">
                  <InputGroup size="sm">
                    <InputGroup.Text>
                      <Search size={14} />
                    </InputGroup.Text>
                    <Form.Control
                      placeholder="Search label"
                      value={labelsFilterSearchQuery}
                      onChange={(e) => setLabelsFilterSearchQuery(e.target.value)}
                      onClick={(e) => e.stopPropagation()}
                      style={{ fontSize: '0.75rem' }}
                    />
                  </InputGroup>
                </div>
                <Dropdown.Item
                  onClick={() => {
                    setSelectedTag('');
                    setShowTagsFilter(false);
                    setLabelsFilterSearchQuery('');
                  }}
                  style={{ fontSize: '0.75rem' }}
                >
                  <div className="d-flex align-items-center">
                    {!selectedTag && <Check className="me-2" size={14} style={{ color: '#0d6efd' }} />}
                    <span>All labels</span>
                  </div>
                </Dropdown.Item>
                <Dropdown.Divider />
                {allAvailableLabels
                  .filter((label) => label.toLowerCase().includes(labelsFilterSearchQuery.toLowerCase()))
                  .map((label) => (
                    <Dropdown.Item
                      key={label}
                      onClick={() => {
                        setSelectedTag(label);
                        setShowTagsFilter(false);
                        setLabelsFilterSearchQuery('');
                      }}
                      style={{ fontSize: '0.75rem' }}
                    >
                      <div className="d-flex align-items-center">
                        {selectedTag === label && <Check className="me-2" size={14} style={{ color: '#0d6efd' }} />}
                        <Badge bg={getLabelVariant(label)} style={{ fontSize: '0.6rem', padding: '0.15rem 0.4rem' }}>
                          {label}
                        </Badge>
                      </div>
                    </Dropdown.Item>
                  ))}
                <Dropdown.Divider />
                <Dropdown.Item
                  onClick={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    handleCreateFilterLabel();
                  }}
                  style={{ fontSize: '0.75rem', color: '#0d6efd' }}
                >
                  <div className="d-flex align-items-center">
                    <Plus className="me-2" size={14} />
                    <span>Add new label</span>
                  </div>
                </Dropdown.Item>
              </Dropdown.Menu>
            </Dropdown>
            <Dropdown
              show={showOwnerFilter}
              autoClose="outside"
              onToggle={(isOpen) => {
                setShowOwnerFilter(isOpen);
                if (isOpen) {
                  setOwnerFilterSearchQuery('');
                  setOwnerFilterTab('owners');
                  setHoveredOwnerRowKey(null);
                } else {
                  setHoveredOwnerRowKey(null);
                }
              }}
            >
              <Dropdown.Toggle
                variant="outline-secondary"
                size="sm"
                style={{ fontSize: '0.75rem', padding: '0.25rem 0.5rem' }}
              >
                {selectedOwnerButtonLabel}
              </Dropdown.Toggle>
              <Dropdown.Menu style={{ width: '320px', padding: 0, borderRadius: '0.5rem', overflow: 'hidden' }}>
                <div className="p-2 border-bottom" onClick={(event) => event.stopPropagation()}>
                  <InputGroup size="sm">
                    <InputGroup.Text style={{ backgroundColor: '#fff', borderRight: 'none' }}>
                      <Search size={14} />
                    </InputGroup.Text>
                    <Form.Control
                      placeholder="Search owner or filter"
                      value={ownerFilterSearchQuery}
                      onChange={(event) => setOwnerFilterSearchQuery(event.target.value)}
                      style={{ borderLeft: 'none', fontSize: '0.75rem' }}
                    />
                  </InputGroup>
                </div>
                <div className="d-flex border-bottom" onClick={(event) => event.stopPropagation()}>
                  <button
                    type="button"
                    onClick={() => setOwnerFilterTab('favorites')}
                    style={{
                      flex: 1,
                      border: 'none',
                      borderBottom: ownerFilterTab === 'favorites' ? '2px solid #0d6efd' : '2px solid transparent',
                      backgroundColor: 'transparent',
                      color: ownerFilterTab === 'favorites' ? '#0d6efd' : '#6c757d',
                      fontSize: '0.75rem',
                      fontWeight: 600,
                      padding: '0.5rem 0.25rem',
                      display: 'flex',
                      flexDirection: 'column',
                      alignItems: 'center',
                      gap: '0.15rem',
                    }}
                  >
                    <Star size={15} />
                    <span>Favorites</span>
                  </button>
                  <button
                    type="button"
                    onClick={() => setOwnerFilterTab('owners')}
                    style={{
                      flex: 1,
                      border: 'none',
                      borderBottom: ownerFilterTab === 'owners' ? '2px solid #0d6efd' : '2px solid transparent',
                      backgroundColor: 'transparent',
                      color: ownerFilterTab === 'owners' ? '#0d6efd' : '#6c757d',
                      fontSize: '0.75rem',
                      fontWeight: 600,
                      padding: '0.5rem 0.25rem',
                      display: 'flex',
                      flexDirection: 'column',
                      alignItems: 'center',
                      gap: '0.15rem',
                    }}
                  >
                    <Person size={15} />
                    <span>Owners</span>
                  </button>
                  <button
                    type="button"
                    onClick={() => setOwnerFilterTab('filters')}
                    style={{
                      flex: 1,
                      border: 'none',
                      borderBottom: ownerFilterTab === 'filters' ? '2px solid #0d6efd' : '2px solid transparent',
                      backgroundColor: 'transparent',
                      color: ownerFilterTab === 'filters' ? '#0d6efd' : '#6c757d',
                      fontSize: '0.75rem',
                      fontWeight: 600,
                      padding: '0.5rem 0.25rem',
                      display: 'flex',
                      flexDirection: 'column',
                      alignItems: 'center',
                      gap: '0.15rem',
                    }}
                  >
                    <Funnel size={15} />
                    <span>Filters</span>
                  </button>
                </div>
                <div style={{ maxHeight: '280px', overflowY: 'auto', overflowX: 'hidden' }} onClick={(event) => event.stopPropagation()}>
                  {ownerFilterTab === 'owners' && (
                    <div className="py-1">
                      {visibleOwnerOptions.length === 0 ? (
                        <div className="px-3 py-2 text-muted" style={{ fontSize: '0.75rem' }}>
                          No owners found.
                        </div>
                      ) : (
                        visibleOwnerOptions.map((ownerOption) => {
                          const isEveryone = ownerOption.key === normalizeOwnerKey(DEFAULT_OWNER_FILTER_LABEL);
                          const isFavorite = favoriteOwnerKeys.some((entry) => normalizeOwnerKey(entry) === ownerOption.key);
                          const isSelected = normalizeOwnerKey(selectedOwner) === ownerOption.key;
                          const isRowHovered = hoveredOwnerRowKey === ownerOption.key;
                          const rowTextColor = isRowHovered ? '#ffffff' : '#212529';
                          return (
                            <button
                              key={ownerOption.key}
                              type="button"
                              onClick={() => {
                                setSelectedOwner(ownerOption.label);
                                setShowOwnerFilter(false);
                              }}
                              onMouseEnter={() => setHoveredOwnerRowKey(ownerOption.key)}
                              onMouseLeave={() => setHoveredOwnerRowKey((prev) => (prev === ownerOption.key ? null : prev))}
                              style={{
                                width: '100%',
                                border: 'none',
                                backgroundColor: isRowHovered ? '#0d6efd' : 'transparent',
                                textAlign: 'left',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'space-between',
                                padding: '0.55rem 0.75rem',
                                fontSize: '0.8rem',
                                borderRadius: '0.35rem',
                                boxSizing: 'border-box',
                              }}
                            >
                              <div className="d-flex align-items-center gap-2">
                                <Person size={14} style={{ color: isRowHovered ? '#ffffff' : '#6c757d' }} />
                                <span style={{ fontWeight: isEveryone ? 600 : 500, color: rowTextColor }}>{ownerOption.label}</span>
                              </div>
                              <div className="d-flex align-items-center gap-2">
                                {isEveryone ? (
                                  <span style={{ color: isRowHovered ? '#ffffff' : '#adb5bd', lineHeight: 1, display: 'inline-flex' }}>
                                    <Star size={14} />
                                  </span>
                                ) : (
                                  <span
                                    role="button"
                                    tabIndex={0}
                                    onClick={(event) => {
                                      event.preventDefault();
                                      event.stopPropagation();
                                      toggleFavoriteOwner(ownerOption.key);
                                    }}
                                    onKeyDown={(event) => {
                                      if (event.key === 'Enter' || event.key === ' ') {
                                        event.preventDefault();
                                        event.stopPropagation();
                                        toggleFavoriteOwner(ownerOption.key);
                                      }
                                    }}
                                    style={{
                                      color: isFavorite ? '#f4b400' : (isRowHovered ? '#ffffff' : '#adb5bd'),
                                      lineHeight: 1,
                                      display: 'inline-flex',
                                    }}
                                  >
                                    {isFavorite ? <StarFill size={14} /> : <Star size={14} />}
                                  </span>
                                )}
                                {isSelected ? <Check size={15} style={{ color: isRowHovered ? '#ffffff' : '#111827' }} /> : null}
                              </div>
                            </button>
                          );
                        })
                      )}
                    </div>
                  )}
                  {ownerFilterTab === 'filters' && (
                    <div className="py-1">
                      {[
                        { id: 'global', label: 'Global (everyone)', description: 'Only records visible to everyone' },
                        { id: 'personal', label: 'Personal (private)', description: 'Only personally scoped records' },
                      ]
                        .filter((option) => {
                          const query = ownerFilterSearchQuery.trim().toLowerCase();
                          if (!query) return true;
                          return `${option.label} ${option.description}`.toLowerCase().includes(query);
                        })
                        .map((option) => {
                          const isSelected =
                            option.id === 'global'
                              ? normalizeOwnerKey(selectedOwner) === normalizeOwnerKey(DEFAULT_OWNER_FILTER_LABEL)
                              : normalizeOwnerKey(selectedOwner) === normalizeOwnerKey(selfOwnerFilterLabel);
                          return (
                            <button
                              key={`client-owner-filter-${option.id}`}
                              type="button"
                              onClick={() => {
                                setSelectedOwner(option.id === 'global' ? DEFAULT_OWNER_FILTER_LABEL : selfOwnerFilterLabel);
                                setShowOwnerFilter(false);
                              }}
                              style={{
                                width: '100%',
                                border: 'none',
                                backgroundColor: 'transparent',
                                textAlign: 'left',
                                display: 'flex',
                                alignItems: 'flex-start',
                                justifyContent: 'space-between',
                                gap: '0.5rem',
                                padding: '0.6rem 0.75rem',
                              }}
                            >
                              <div>
                                <div style={{ fontSize: '0.79rem', fontWeight: 500, color: '#212529' }}>{option.label}</div>
                                <div style={{ fontSize: '0.69rem', color: '#6c757d', marginTop: '0.15rem' }}>{option.description}</div>
                              </div>
                              {isSelected ? <Check size={15} style={{ color: '#111827' }} /> : null}
                            </button>
                          );
                        })}
                    </div>
                  )}
                  {ownerFilterTab === 'favorites' && (
                    <div className="py-1">
                      {visibleFavoriteOwnerOptions.length === 0 ? (
                        <div className="px-3 py-2 text-muted" style={{ fontSize: '0.75rem' }}>
                          No favorite owners yet.
                        </div>
                      ) : (
                        visibleFavoriteOwnerOptions.map((ownerOption) => {
                          const isSelected = normalizeOwnerKey(selectedOwner) === ownerOption.key;
                          return (
                            <button
                              key={`client-favorite-owner-${ownerOption.key}`}
                              type="button"
                              onClick={() => {
                                setSelectedOwner(ownerOption.label);
                                setShowOwnerFilter(false);
                              }}
                              style={{
                                width: '100%',
                                border: 'none',
                                backgroundColor: 'transparent',
                                textAlign: 'left',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'space-between',
                                padding: '0.55rem 0.75rem',
                                fontSize: '0.8rem',
                              }}
                            >
                              <div className="d-flex align-items-center gap-2">
                                <Person size={14} style={{ color: '#6c757d' }} />
                                <span style={{ fontWeight: 500, color: '#212529' }}>{ownerOption.label}</span>
                              </div>
                              <div className="d-flex align-items-center gap-2">
                                <StarFill size={14} style={{ color: '#f4b400' }} />
                                {isSelected ? <Check size={15} style={{ color: '#111827' }} /> : null}
                              </div>
                            </button>
                          );
                        })
                      )}
                    </div>
                  )}
                </div>
              </Dropdown.Menu>
            </Dropdown>
            <Button variant="link" size="sm" className="p-2">
              <ThreeDotsVertical size={18} />
            </Button>
          </div>
        </div>

        {/* Clients Table - wrap in Card like Leads, same thead styling */}
        <Card className="crm-table-card">
          <Card.Body className="p-0">
            <Table responsive hover className="mb-0 crm-table" style={{ fontSize: '0.75rem' }}>
              <thead className="table-light">
                <tr>
                  <th style={{ width: '50px', fontSize: '0.75rem' }}>
                    <Form.Check
                      type="checkbox"
                      checked={selectedClients.size === filteredClients.length && filteredClients.length > 0}
                      onChange={handleSelectAll}
                    />
                  </th>
            <th style={{ fontSize: '0.75rem', verticalAlign: 'middle' }}>Name</th>
            <th style={{ width: '48px', fontSize: '0.75rem', whiteSpace: 'nowrap', verticalAlign: 'middle', textAlign: 'center' }} title="Decision Maker">DM</th>
            <th style={{ width: '48px', fontSize: '0.75rem', whiteSpace: 'nowrap', verticalAlign: 'middle' }}>Renewal</th>
            <th style={{ fontSize: '0.75rem', verticalAlign: 'middle' }}>Organization</th>
            <th style={{ fontSize: '0.75rem' }}>Industry</th>
            <th style={{ fontSize: '0.75rem' }}>Email</th>
            <th style={{ fontSize: '0.75rem' }}>Phone</th>
            <th style={{ fontSize: '0.75rem' }}>Status</th>
            <th style={{ fontSize: '0.75rem' }}>
              Labels
              <span className="ms-1">↑</span>
            </th>
            <th style={{ fontSize: '0.75rem' }}>Client created</th>
            <th style={{ width: '40px', fontSize: '0.75rem' }}>
              <Button
                variant="link"
                size="sm"
                className="p-0"
                onClick={() => setShowCustomizeColumnsModal(true)}
                style={{ fontSize: '0.75rem' }}
              >
                <Gear size={14} />
              </Button>
            </th>
          </tr>
        </thead>
        <tbody>
          {filteredClients.length === 0 ? (
            <tr>
              <td colSpan={12} className="text-center py-5 text-muted" style={{ fontSize: '0.75rem' }}>
                No clients found
              </td>
            </tr>
          ) : (
          filteredClients.map((client: Client) => (
            <tr 
              key={client.id} 
              onClick={() => handleClientClick(client)}
              style={{ cursor: 'pointer' }}
            >
              <td onClick={(e) => handleSelectClient(client.id, e)}>
                <Form.Check
                  checked={selectedClients.has(client.id)}
                  onChange={() => {}}
                />
              </td>
              <td>{client.name}</td>
              <td style={{ textAlign: 'center', verticalAlign: 'middle' }}>
                {client.decisionMaker ? (
                  <span title="Decision Maker" style={{ color: '#0d6efd', display: 'inline-flex', alignItems: 'center' }}>
                    <DiamondFill size={14} />
                  </span>
                ) : null}
              </td>
              <td
                className={isContractRenewalOverdue(client.contractRenewalDate) ? 'text-danger' : undefined}
                style={{ whiteSpace: 'nowrap', fontSize: '0.75rem', verticalAlign: 'middle' }}
              >
                {formatCompactRenewalLabelFromRawDate(client.contractRenewalDate)}
              </td>
              <td>{client.organization || '-'}</td>
              <td>{client.industry || '-'}</td>
              <td>{client.email || '-'}</td>
              <td>{client.phone || '-'}</td>
              <td>
                <Badge bg={client.status === 'current' ? 'success' : 'secondary'} style={{ fontSize: '0.6rem', padding: '0.15rem 0.35rem' }}>
                  {client.status === 'current' ? 'Current' : 'Past'}
                </Badge>
              </td>
              <td>
                <div className="d-flex flex-wrap gap-1">
                  {(client.tags || []).map((label, idx) => (
                    <Badge key={`${client.id}-label-${idx}`} bg={getLabelVariant(label)} style={{ fontSize: '0.6rem', padding: '0.15rem 0.35rem' }}>
                      {label}
                    </Badge>
                  ))}
                </div>
              </td>
              <td>
                {client.createdAt 
                  ? toLocaleDateStringInTimezone(client.createdAt, 'en-US', { 
                      month: 'short', 
                      day: 'numeric', 
                      year: 'numeric' 
                    }, tenantTimezone)
                  : '-'
                }
              </td>
              <td></td>
            </tr>
          ))
          )}
        </tbody>
      </Table>
          </Card.Body>
        </Card>

      {/* Add Client Modal */}
      <Modal show={showAddClientModal} onHide={() => setShowAddClientModal(false)} size="lg">
        <Modal.Header style={{ fontSize: '1rem', padding: '0.75rem 1rem' }}>
          <Modal.Title>Add client</Modal.Title>
          <Button variant="link" onClick={() => setShowAddClientModal(false)} style={{ padding: 0, fontSize: '18px' }}>
            <X />
          </Button>
        </Modal.Header>
        <Modal.Body style={{ fontSize: '0.75rem', padding: '1rem' }}>
          <Row>
            <Col md={6}>
              <Form.Group className="mb-3">
                <Form.Label style={{ fontSize: '0.75rem' }}>First Name *</Form.Label>
                <Form.Control
                  size="sm"
                  value={formData.firstName}
                  onChange={(e) => handleFormChange('firstName', e.target.value)}
                  style={{ fontSize: '0.75rem', padding: '0.25rem 0.5rem' }}
                />
              </Form.Group>
            </Col>
            <Col md={6}>
              <Form.Group className="mb-3">
                <Form.Label style={{ fontSize: '0.75rem' }}>Last Name *</Form.Label>
                <Form.Control
                  size="sm"
                  value={formData.lastName}
                  onChange={(e) => handleFormChange('lastName', e.target.value)}
                  style={{ fontSize: '0.75rem', padding: '0.25rem 0.5rem' }}
                />
              </Form.Group>
            </Col>
            <Col md={6}>
              <Form.Group className="mb-3">
                <Form.Label style={{ fontSize: '0.75rem' }}>Account Name</Form.Label>
                <Form.Control
                  size="sm"
                  value={formData.organization}
                  onChange={(e) => handleFormChange('organization', e.target.value)}
                  style={{ fontSize: '0.75rem', padding: '0.25rem 0.5rem' }}
                />
              </Form.Group>
            </Col>
            <Col md={6}>
              <Form.Group className="mb-3">
                <Form.Label style={{ fontSize: '0.75rem' }}>Contact Stage</Form.Label>
                <Form.Select
                  size="sm"
                  value={formData.contactStage}
                  onChange={(e) => handleFormChange('contactStage', e.target.value)}
                  style={{ fontSize: '0.75rem', padding: '0.25rem 0.5rem' }}
                >
                  <option value="">Select</option>
                  {contactStageOptions.map((stage) => (
                    <option key={`client-contact-stage-${stage}`} value={stage}>
                      {stage}
                    </option>
                  ))}
                </Form.Select>
              </Form.Group>
            </Col>
            <Col md={6}>
              <Form.Group className="mb-3">
                <Form.Label style={{ fontSize: '0.75rem' }}>Industry</Form.Label>
                <Form.Control
                  size="sm"
                  value={formData.industry}
                  onChange={(e) => handleFormChange('industry', e.target.value)}
                  style={{ fontSize: '0.75rem', padding: '0.25rem 0.5rem' }}
                />
              </Form.Group>
            </Col>
            <Col md={6}>
              <Form.Group className="mb-3">
                <Form.Label style={{ fontSize: '0.75rem' }}>Account Stage</Form.Label>
                <Form.Select
                  size="sm"
                  value={formData.accountStage}
                  onChange={(e) => handleFormChange('accountStage', e.target.value)}
                  style={{ fontSize: '0.75rem', padding: '0.25rem 0.5rem' }}
                >
                  <option value="">Select</option>
                  {accountStageOptions.map((stage) => (
                    <option key={`client-account-stage-${stage}`} value={stage}>
                      {stage}
                    </option>
                  ))}
                </Form.Select>
              </Form.Group>
            </Col>
            <Col md={6}>
              <Form.Group className="mb-3">
                <Form.Label style={{ fontSize: '0.75rem' }}>Visibility</Form.Label>
                <Form.Select
                  size="sm"
                  value={formData.visibility}
                  onChange={(e) => handleFormChange('visibility', e.target.value)}
                  style={{ fontSize: '0.75rem', padding: '0.25rem 0.5rem' }}
                >
                  <option value="global">Global (tenant-wide)</option>
                  <option value="personal">Personal (private)</option>
                </Form.Select>
              </Form.Group>
            </Col>
            <Col md={6}>
              <Form.Group className="mb-3">
                <Form.Label style={{ fontSize: '0.75rem' }}>Website</Form.Label>
                <Form.Control
                  size="sm"
                  value={formData.website}
                  onChange={(e) => handleFormChange('website', e.target.value)}
                  style={{ fontSize: '0.75rem', padding: '0.25rem 0.5rem' }}
                />
              </Form.Group>
            </Col>
            <Col md={6}>
              <Form.Group className="mb-3">
                <Form.Label style={{ fontSize: '0.75rem' }}>Email</Form.Label>
                <Form.Control
                  type="email"
                  size="sm"
                  value={formData.email}
                  onChange={(e) => handleFormChange('email', e.target.value)}
                  style={{ fontSize: '0.75rem', padding: '0.25rem 0.5rem' }}
                />
              </Form.Group>
            </Col>
            <Col md={6}>
              <Form.Group className="mb-3">
                <Form.Label style={{ fontSize: '0.75rem' }}>Phone</Form.Label>
                <Form.Control
                  size="sm"
                  value={formData.phone}
                  onChange={(e) => handleFormChange('phone', e.target.value)}
                  style={{ fontSize: '0.75rem', padding: '0.25rem 0.5rem' }}
                />
              </Form.Group>
            </Col>
            <Col md={6}>
              <Form.Group className="mb-3">
                <Form.Label style={{ fontSize: '0.75rem' }}>Decision Maker</Form.Label>
                <div style={{ paddingTop: '0.25rem' }}>
                  <Form.Check
                    type="switch"
                    id="add-client-decision-maker-switch"
                    label={formData.decisionMaker ? 'Yes' : 'No'}
                    checked={Boolean(formData.decisionMaker)}
                    onChange={(e) => handleFormChange('decisionMaker', e.target.checked)}
                    style={{ fontSize: '0.75rem' }}
                  />
                </div>
              </Form.Group>
            </Col>
            <Col md={12}>
              <Form.Group className="mb-3">
                <Form.Label style={{ fontSize: '0.75rem' }}>Address</Form.Label>
                <Form.Control
                  size="sm"
                  value={formData.address}
                  onChange={(e) => handleFormChange('address', e.target.value)}
                  style={{ fontSize: '0.75rem', padding: '0.25rem 0.5rem' }}
                />
              </Form.Group>
            </Col>
            <Col md={4}>
              <Form.Group className="mb-3">
                <Form.Label style={{ fontSize: '0.75rem' }}>City</Form.Label>
                <Form.Control
                  size="sm"
                  value={formData.city}
                  onChange={(e) => handleFormChange('city', e.target.value)}
                  style={{ fontSize: '0.75rem', padding: '0.25rem 0.5rem' }}
                />
              </Form.Group>
            </Col>
            <Col md={4}>
              <Form.Group className="mb-3">
                <Form.Label style={{ fontSize: '0.75rem' }}>State</Form.Label>
                <Form.Control
                  size="sm"
                  value={formData.state}
                  onChange={(e) => handleFormChange('state', e.target.value)}
                  style={{ fontSize: '0.75rem', padding: '0.25rem 0.5rem' }}
                />
              </Form.Group>
            </Col>
            <Col md={4}>
              <Form.Group className="mb-3">
                <Form.Label style={{ fontSize: '0.75rem' }}>Zip Code</Form.Label>
                <Form.Control
                  size="sm"
                  value={formData.zipCode}
                  onChange={(e) => handleFormChange('zipCode', e.target.value)}
                  style={{ fontSize: '0.75rem', padding: '0.25rem 0.5rem' }}
                />
              </Form.Group>
            </Col>
            <Col md={6}>
              <Form.Group className="mb-3">
                <Form.Label style={{ fontSize: '0.75rem' }}>Status</Form.Label>
                <Form.Select
                  size="sm"
                  value={formData.status}
                  onChange={(e) => handleFormChange('status', e.target.value)}
                  style={{ fontSize: '0.75rem', padding: '0.25rem 0.5rem' }}
                >
                  <option value="current">Current</option>
                  <option value="past">Past</option>
                </Form.Select>
              </Form.Group>
            </Col>
            {formData.status === 'past' && (
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label style={{ fontSize: '0.75rem' }}>Reason (why no longer a client)</Form.Label>
                  <Form.Control
                    size="sm"
                    value={formData.pastClientReason}
                    onChange={(e) => handleFormChange('pastClientReason', e.target.value)}
                    style={{ fontSize: '0.75rem', padding: '0.25rem 0.5rem' }}
                  />
                </Form.Group>
              </Col>
            )}
            <Col md={12}>
              <Form.Group className="mb-3">
                <Form.Label style={{ fontSize: '0.75rem' }}>Notes</Form.Label>
                <Form.Control
                  as="textarea"
                  rows={3}
                  size="sm"
                  value={formData.notes}
                  onChange={(e) => handleFormChange('notes', e.target.value)}
                  style={{ fontSize: '0.75rem', padding: '0.25rem 0.5rem' }}
                />
              </Form.Group>
            </Col>
          </Row>
        </Modal.Body>
        <Modal.Footer style={{ fontSize: '0.75rem', padding: '0.75rem 1rem' }}>
          <div className="d-flex justify-content-between align-items-center w-100">
            <span style={{ fontSize: '0.75rem' }}>
              {clients.length}/unlimited
            </span>
            <div className="d-flex gap-2">
              <Button variant="secondary" size="sm" onClick={() => setShowAddClientModal(false)}>
                Cancel
              </Button>
              <Button 
                variant="primary" 
                size="sm" 
                onClick={handleSaveClient}
                disabled={
                  isCreatingClient
                  || !String(formData.firstName || '').trim()
                  || !String(formData.lastName || '').trim()
                }
              >
                {isCreatingClient ? <Spinner size="sm" animation="border" /> : 'Save'}
              </Button>
            </div>
          </div>
        </Modal.Footer>
      </Modal>

      {/* Client Detail Sidebar - matches Leads layout exactly */}
      <Offcanvas
        show={showSidebar}
        onHide={() => setShowSidebar(false)}
        placement="end"
        style={{
          width: 'calc(100vw - 510px)',
          maxWidth: '1200px',
          minWidth: '800px',
          top: '56px',
          height: 'calc(100vh - 56px)',
          zIndex: 1040
        }}
      >
        <Offcanvas.Header
          className="position-relative"
          style={{
            borderBottom: '1px solid #dee2e6',
            paddingTop: '0.95rem',
            paddingBottom: '1.05rem',
          }}
        >
          <Offcanvas.Title style={{ flex: 1 }}>
            {selectedClient && (
              <div>
                <div className="fw-bold" style={{ fontSize: '0.85rem' }}>
                  {selectedClient.name || 'Untitled client'}
                </div>
                {String(selectedClient.organization || '').trim().length > 0 && (
                  <div className="text-muted small mt-1" style={{ fontSize: '0.7rem' }}>
                    {selectedClient.organization}
                  </div>
                )}
              </div>
            )}
          </Offcanvas.Title>
          <Button
            variant="link"
            className="p-0 position-absolute"
            onClick={() => { setShowSidebar(false); setSelectedClient(null); }}
            style={{ top: '1.4rem', right: '1rem', zIndex: 10 }}
          >
            <X size={20} />
          </Button>
        </Offcanvas.Header>
        <Offcanvas.Body className="p-0">
          {selectedClient && (
            <div className="d-flex h-100" style={{ overflowX: 'hidden' }}>
              {/* Left Column - Details (same structure as Leads) */}
              <div className="crm-sidebar-left-column" style={{ width: '320px', padding: '1rem', borderRight: '1px solid #dee2e6', overflowY: 'auto', display: 'flex', flexDirection: 'column' }}>
                <div className="mb-4" style={{ flex: 1 }}>
                  <div className="mb-3">
                    <div className="d-flex align-items-center gap-2 mb-2">
                      <h6 className="text-uppercase text-muted small mb-0" style={{ fontSize: '0.75rem', fontWeight: 'bold' }}>DETAILS</h6>
                      <Button variant="link" size="sm" className="p-0" style={{ fontSize: '0.75rem' }} onClick={() => setShowAllFields(!showAllFields)}>
                        {showAllFields ? 'Hide fields' : 'Show all fields'}
                      </Button>
                    </div>
                    {showAllFields && (
                      <div className="mb-2 d-flex align-items-center gap-2" style={{ padding: '0.5rem', borderRadius: '4px' }}>
                        <span style={detailFieldIconStyle}>
                          <FileText size={14} />
                        </span>
                        <span style={{ fontSize: '0.75rem', color: '#6c757d' }}>Add to sequence</span>
                      </div>
                    )}

                    {renderEditableLabelsRow(
                      'detailsLabels',
                      showAllFields
                    )}
                    {renderEditableClientRow('value', '$ Value', CurrencyDollar, { showAll: showAllFields })}
                    {renderEditableClientRow('expectedCloseDate', 'Expected close date', Calendar, { showAll: showAllFields })}
                    {renderEditableClientRow('businessUnit', 'Business Unit:', Briefcase, { showAll: showAllFields })}

                    {renderEditableClientRow('owner', 'Owner', Person, { showAll: true })}
                    {renderEditableClientRow('visibility', 'Visibility', PeopleFill, { showAll: true })}

                    {renderEditableClientRow('sourceOrigin', 'Source origin', ArrowRight, { showAll: showAllFields, showInfo: true })}
                    {renderEditableClientRow('sourceChannel', 'Source channel', ArrowRight, { showAll: showAllFields })}
                    {renderEditableClientRow('sourceChannelId', 'Source channel ID', Dot, { showAll: showAllFields })}
                  </div>

                  <div className="mb-3" style={{ marginTop: '2rem' }}>
                    <div className="d-flex align-items-center gap-2 mb-2">
                      <h6 className="text-uppercase text-muted small mb-0" style={{ fontSize: '0.75rem', fontWeight: 'bold' }}>PERSON</h6>
                      <Button variant="link" size="sm" className="p-0" style={{ fontSize: '0.75rem' }} onClick={() => setShowAllPersonFields(!showAllPersonFields)}>
                        {showAllPersonFields ? 'Hide fields' : 'Show all fields'}
                      </Button>
                    </div>

                    {renderEditableClientRow('name', 'No name', Person, { showAll: showAllPersonFields })}
                    {renderEditableClientRow('title', 'Title', FileText, { showAll: showAllPersonFields })}
                    {selectedClient && (
                      <div className="d-flex align-items-center gap-2 mb-2" style={{ padding: '0.5rem', borderRadius: '4px' }}>
                        <span style={detailFieldIconStyle}>
                          {selectedClient.decisionMaker
                            ? <DiamondFill size={14} style={{ color: '#0d6efd' }} />
                            : <Diamond size={14} />}
                        </span>
                        <span style={{ fontSize: '0.75rem', color: '#212529', flex: 1 }}>Decision Maker</span>
                        <Form.Check
                          type="switch"
                          id={`client-decision-maker-switch-${selectedClient.id}`}
                          checked={Boolean(selectedClient.decisionMaker)}
                          onChange={async (e) => {
                            const next = e.target.checked;
                            if (Boolean(selectedClient.decisionMaker) === next) return;
                            try {
                              await updateClient({ clientId: selectedClient.id, decisionMaker: next } as any).unwrap();
                              setSelectedClient({ ...selectedClient, decisionMaker: next });
                            } catch (error: any) {
                              console.error('Error updating decisionMaker:', error);
                              alert(`Failed to update Decision Maker: ${error?.data?.message || error?.message || 'Unknown error'}`);
                            }
                          }}
                          style={{ fontSize: '0.75rem' }}
                        />
                      </div>
                    )}
                    {renderEditableClientRow('contactStage', 'Contact Stage', CheckSquare, { showAll: showAllPersonFields })}
                    {renderEditableClientRow('email', 'No email', Envelope, { showAll: showAllPersonFields })}
                    {renderEditableClientRow('phone', 'No phone', Phone, { showAll: showAllPersonFields })}
                    {renderEditableClientRow('workPhone', 'Work phone', Phone, { showAll: showAllPersonFields })}
                    {renderEditableClientRow('cellPhone', 'Cell phone', Phone, { showAll: showAllPersonFields })}
                    {renderEditableClientRow('assistantName', "Assistant's Name", Person, { showAll: showAllPersonFields })}
                    {renderEditableClientRow('assistantPhone', "Assistant's Phone", Phone, { showAll: showAllPersonFields })}
                    {renderEditableClientRow('assistantEmail', "Assistant's Email", Envelope, { showAll: showAllPersonFields })}
                  </div>

                  <div className="mb-3" style={{ marginTop: '2rem' }}>
                    <div className="d-flex align-items-center gap-2 mb-2">
                      <h6 className="text-uppercase text-muted small mb-0" style={{ fontSize: '0.75rem', fontWeight: 'bold' }}>ORGANIZATION</h6>
                      <Button variant="link" size="sm" className="p-0" style={{ fontSize: '0.75rem' }} onClick={() => setShowAllOrganizationFields(!showAllOrganizationFields)}>
                        {showAllOrganizationFields ? 'Hide fields' : 'Show all fields'}
                      </Button>
                    </div>

                    {renderEditableClientRow('organization', 'No organization', BuildingFill, { showAll: showAllOrganizationFields })}
                    {renderEditableClientRow('contractRenewalDate', '+ Add Contract Renewal', Calendar, { showAll: true })}
                    {renderEditableClientRow('accountStage', 'Account Stage', Briefcase, { showAll: showAllOrganizationFields })}
                    {renderEditableClientRow('address', 'Address', GeoAltFill, { showAll: showAllOrganizationFields })}
                    {renderEditableClientRow('website', 'Website', Globe2, { showAll: showAllOrganizationFields })}
                    {renderEditableClientRow('linkedInProfile', 'LinkedIn profile', Linkedin, { showAll: showAllOrganizationFields })}
                    {renderEditableClientRow('numberOfEmployees', 'Number of employees', PeopleFill, { showAll: showAllOrganizationFields })}
                    {renderEditableClientRow('annualRevenue', 'Annual revenue', CurrencyDollar, { showAll: showAllOrganizationFields })}
                    {renderEditableClientRow('industry', 'Industry', Briefcase, { showAll: showAllOrganizationFields })}
                  </div>
                </div>
                <div style={{ marginTop: 'auto', paddingTop: '1rem', borderTop: '1px solid #dee2e6' }}>
                  <div className="d-flex align-items-center gap-2">
                    <Dropdown align="end">
                      <Dropdown.Toggle
                        as={IconOnlyDropdownToggle}
                        id="client-sidebar-actions"
                      >
                        <ThreeDotsVertical size={14} />
                      </Dropdown.Toggle>
                      <Dropdown.Menu>
                        <Dropdown.Item
                          onClick={handleDeleteSelectedClient}
                          style={{ color: '#dc3545' }}
                          disabled={isDeletingClient}
                        >
                          Delete client
                        </Dropdown.Item>
                      </Dropdown.Menu>
                    </Dropdown>
                    <OverlayTrigger
                      placement="top"
                      overlay={
                        <Tooltip id="archive-client-tooltip">
                          {selectedClient && isClientArchived(selectedClient) ? 'Move to inbox' : 'Archive'}
                        </Tooltip>
                      }
                    >
                      <Button
                        variant="outline-secondary"
                        size="sm"
                        onClick={handleArchiveSelectedClient}
                        disabled={isUpdatingClient}
                        style={{
                          width: '32px',
                          height: '32px',
                          padding: 0,
                          display: 'inline-flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          borderColor: '#ced4da'
                        }}
                      >
                        <Archive size={14} />
                      </Button>
                    </OverlayTrigger>
                    <Dropdown align="end" drop="up">
                      <Dropdown.Toggle
                        variant="primary"
                        size="sm"
                        id="client-convert-actions"
                        disabled={!selectedClient || isConvertingClient}
                        style={{ fontSize: '0.7rem', padding: '0.35rem 0.75rem', width: 'auto', borderRadius: '10px' }}
                      >
                        <Folder className="me-1" size={12} />
                        {isConvertingClient ? 'Converting...' : 'Convert'}
                      </Dropdown.Toggle>
                      <Dropdown.Menu
                        style={{
                          minWidth: '240px',
                          padding: '0.35rem',
                          borderRadius: '12px',
                          border: '1px solid #e9ecef',
                          boxShadow: '0 14px 32px rgba(15, 23, 42, 0.14)'
                        }}
                      >
                        {([
                          { target: 'Contact' as const, label: 'Convert to Contact', color: '#0d6efd' },
                          { target: 'Lead' as const, label: 'Convert to Lead', color: '#6c757d' },
                          { target: 'Deal' as const, label: 'Convert to Deal', color: '#0d6efd' },
                        ]).map((option, index) => (
                          <React.Fragment key={option.target}>
                            <Dropdown.Item
                              onClick={() => handleConvertSelectedClient(option.target)}
                              disabled={isConvertingClient}
                              className="d-flex align-items-center"
                              style={{
                                fontSize: '0.7rem',
                                fontWeight: 500,
                                padding: '0.5rem 0.8rem',
                                borderRadius: '8px',
                                lineHeight: 1.2
                              }}
                            >
                              <Lightning size={14} className="me-2" style={{ color: option.color }} />
                              {option.label}
                            </Dropdown.Item>
                            {index < 2 && <Dropdown.Divider style={{ margin: '0.15rem 0' }} />}
                          </React.Fragment>
                        ))}
                      </Dropdown.Menu>
                    </Dropdown>
                  </div>
                </div>
              </div>

              {/* Right Column - Notes/Activity/Email/Files (same as Leads) */}
              <div style={{ flex: 1, padding: '1rem', overflowY: 'auto', overflowX: 'hidden' }}>
                <Tabs activeKey={activeTab} onSelect={(k) => k && setActiveTab(k)} className="mb-3" style={{ fontSize: '0.75rem' }}>
                  <Tab eventKey="notes" title="Notes">
                    <div
                      className="mb-3 note-editor-container"
                      onClick={() => { if (noteEditorRef.current && !isNoteEditorFocused) noteEditorRef.current.focus(); }}
                      style={{
                        backgroundColor: isNoteEditorFocused ? '#fff9e6' : '#ffffff',
                        border: isNoteEditorFocused ? '2px solid #0d6efd' : '1px solid #dee2e6',
                        borderRadius: '4px',
                        transition: 'all 0.2s',
                        cursor: 'text'
                      }}
                    >
                      <div
                        ref={noteEditorRef}
                        contentEditable
                        onFocus={() => setIsNoteEditorFocused(true)}
                        onBlur={() => setTimeout(() => {
                          const active = document.activeElement;
                          if (!active?.closest('.note-editor-container') && !currentNoteContent.trim()) setIsNoteEditorFocused(false);
                        }, 200)}
                        onInput={(e) => {
                          const content = e.currentTarget.innerHTML;
                          setCurrentNoteContent(content);
                          if (content.trim() === '' || content === '<br>') e.currentTarget.setAttribute('data-placeholder', 'Take a note, @name...');
                          else e.currentTarget.removeAttribute('data-placeholder');
                        }}
                        data-placeholder="Take a note, @name..."
                        style={{ minHeight: '100px', padding: '0.75rem', fontSize: '0.75rem', outline: 'none', backgroundColor: 'transparent', position: 'relative' }}
                        className="note-editor"
                      />
                      {isNoteEditorFocused && (
                        <div className="d-flex align-items-center gap-1 p-2 border-top" style={{ backgroundColor: '#f8f9fa', fontSize: '0.75rem' }} onClick={(e) => e.preventDefault()}>
                          <Button variant="link" size="sm" className="p-1" onClick={(e) => { e.preventDefault(); executeCommand('bold'); }} style={{ color: '#6c757d' }}><TypeBold size={14} /></Button>
                          <Button variant="link" size="sm" className="p-1" onClick={(e) => { e.preventDefault(); executeCommand('italic'); }} style={{ color: '#6c757d' }}><TypeItalic size={14} /></Button>
                          <Button variant="link" size="sm" className="p-1" onClick={(e) => { e.preventDefault(); executeCommand('underline'); }} style={{ color: '#6c757d' }}><TypeUnderline size={14} /></Button>
                          <div className="vr" style={{ height: '20px', margin: '0 0.25rem' }} />
                          <Button variant="link" size="sm" className="p-1" onClick={(e) => { e.preventDefault(); const url = prompt('Enter URL:'); if (url) executeCommand('createLink', url); }} style={{ color: '#6c757d' }}><Link45deg size={14} /></Button>
                          <Button variant="link" size="sm" className="p-1" onClick={(e) => { e.preventDefault(); executeCommand('insertText', '@'); }} style={{ color: '#6c757d' }}><At size={14} /></Button>
                          <div className="vr" style={{ height: '20px', margin: '0 0.25rem' }} />
                          <Button variant="link" size="sm" className="p-1" onClick={(e) => { e.preventDefault(); executeCommand('insertUnorderedList'); }} style={{ color: '#6c757d' }}><ListUl size={14} /></Button>
                          <Button variant="link" size="sm" className="p-1" onClick={(e) => { e.preventDefault(); executeCommand('insertOrderedList'); }} style={{ color: '#6c757d' }}><ListOl size={14} /></Button>
                          <Button variant="link" size="sm" className="p-1" onClick={(e) => { e.preventDefault(); executeCommand('outdent'); }} style={{ color: '#6c757d' }} title="Decrease indent"><ArrowLeft size={14} /></Button>
                          <Button variant="link" size="sm" className="p-1" onClick={(e) => { e.preventDefault(); executeCommand('indent'); }} style={{ color: '#6c757d' }} title="Increase indent"><ArrowRight size={14} /></Button>
                          <div className="vr" style={{ height: '20px', margin: '0 0.25rem' }} />
                          <Button variant="link" size="sm" className="p-1" onClick={(e) => { e.preventDefault(); executeCommand('strikeThrough'); }} style={{ color: '#6c757d' }}><TypeStrikethrough size={14} /></Button>
                          <div className="flex-grow-1" />
                          <Button variant="outline-secondary" size="sm" onClick={handleCancelNote} style={{ fontSize: '0.7rem', padding: '0.25rem 0.5rem' }}>Cancel</Button>
                          <Button variant="primary" size="sm" onClick={handleSaveNote} disabled={!currentNoteContent.trim() && !noteEditorRef.current?.textContent?.trim()} style={{ fontSize: '0.7rem', padding: '0.25rem 0.5rem' }}>Save</Button>
                        </div>
                      )}
                    </div>
                    <div className="mb-3">
                      <h6 className="small mb-2" style={{ fontSize: '0.7rem', fontWeight: 'bold' }}>Focus</h6>
                      {(pinnedFocusNotes.length === 0 && upcomingFocusActivities.length === 0) ? (
                        <p className="text-muted small" style={{ fontSize: '0.7rem' }}>No focus items yet.</p>
                      ) : (
                        <div className="mb-2">
                          {pinnedFocusNotes.map((note) => (
                            <div key={`focus-note-${note.id}`} className="d-flex align-items-start gap-2 mb-2">
                              <div
                                className="d-inline-flex align-items-center justify-content-center border"
                                style={{
                                  width: '32px',
                                  height: '32px',
                                  borderRadius: '999px',
                                  backgroundColor: '#f8f9fa',
                                  color: '#0d6efd',
                                  marginTop: '2px'
                                }}
                              >
                                <Pin size={13} />
                              </div>
                              <div
                                className="flex-grow-1 border rounded"
                                style={{ backgroundColor: '#fff9e6', padding: '0.5rem 0.65rem' }}
                              >
                                <div className="d-flex align-items-start justify-content-between gap-2">
                                  <div
                                    className="fw-semibold"
                                    style={{ fontSize: '0.75rem' }}
                                    dangerouslySetInnerHTML={{ __html: note.content }}
                                  />
                                  <Button
                                    variant="link"
                                    size="sm"
                                    className="p-0"
                                    style={{ color: '#0d6efd' }}
                                    onClick={() => handleTogglePinNote(note.id)}
                                    title="Unpin note"
                                  >
                                    <Pin size={12} />
                                  </Button>
                                </div>
                                <div className="text-muted mt-1" style={{ fontSize: '0.7rem' }}>
                                  {formatDate(note.timestamp)} · {note.author}
                                </div>
                              </div>
                            </div>
                          ))}
                          {upcomingFocusActivities.slice(0, 3).map((meeting) => {
                            const activityType = getScheduledActivityTypeFromMeeting(meeting);
                            return (
                              <div key={meeting.id} className="d-flex align-items-start gap-2 mb-2">
                                <div
                                  className="d-inline-flex align-items-center justify-content-center border"
                                  style={{
                                    width: '32px',
                                    height: '32px',
                                    borderRadius: '999px',
                                    backgroundColor: '#f8f9fa',
                                    color: '#495057',
                                    marginTop: '2px'
                                  }}
                                >
                                  {renderScheduledActivityIcon(activityType)}
                                </div>
                                <div
                                  className="flex-grow-1 border rounded"
                                  style={{ backgroundColor: '#ffffff', padding: '0.5rem 0.65rem' }}
                                >
                                  <div className="d-flex align-items-start justify-content-between gap-2">
                                    <div className="fw-semibold" style={{ fontSize: '0.75rem' }}>
                                      {meeting.title || SCHEDULE_ACTIVITY_LABELS[activityType]}
                                    </div>
                                    <Button variant="link" size="sm" className="p-0 text-muted">
                                      <ThreeDotsVertical size={12} />
                                    </Button>
                                  </div>
                                  <div className="d-flex flex-wrap align-items-center gap-2 text-muted mt-1" style={{ fontSize: '0.7rem' }}>
                                    <span>{formatFocusDateLabel(meeting.startTime)} · PhiLL Kross</span>
                                    <span className="d-inline-flex align-items-center gap-1">
                                      <Building size={11} />
                                      {selectedClient?.name || ''}
                                    </span>
                                    {selectedClient?.organization && (
                                      <span className="d-inline-flex align-items-center gap-1">
                                        <BuildingFill size={11} />
                                        {selectedClient.organization}
                                      </span>
                                    )}
                                  </div>
                                </div>
                              </div>
                            );
                          })}
                        </div>
                      )}
                      <p className="text-muted small mb-2" style={{ fontSize: '0.7rem' }}>Scheduled activities, pinned notes, email drafts and scheduled emails will appear here.</p>
                      <div className="d-flex align-items-stretch mb-2">
                        <Dropdown
                          show={showScheduledActivityTypeDropdown}
                          onToggle={(isOpen) => setShowScheduledActivityTypeDropdown(isOpen)}
                        >
                          <Dropdown.Toggle
                            as={IconOnlyDropdownToggle}
                            id="scheduled-client-activity-type-toggle"
                            style={{
                              width: '62px',
                              height: '31px',
                              borderTopRightRadius: 0,
                              borderBottomRightRadius: 0,
                              borderRight: '0',
                              gap: '0.25rem'
                            }}
                          >
                            {renderScheduledActivityIcon(scheduledActivityType)}
                            <ChevronDown
                              size={9}
                              style={{
                                transition: 'transform 0.2s',
                                transform: showScheduledActivityTypeDropdown ? 'rotate(180deg)' : 'rotate(0deg)'
                              }}
                            />
                          </Dropdown.Toggle>
                          <Dropdown.Menu style={{ minWidth: '170px', padding: '0.25rem' }}>
                            {SCHEDULE_ACTIVITY_OPTIONS.map((option) => {
                              const isSelected = option.type === scheduledActivityType;
                              return (
                                <Dropdown.Item
                                  key={option.type}
                                  onClick={() => handleSelectScheduledActivityType(option.type)}
                                  style={{
                                    fontSize: '0.75rem',
                                    borderRadius: '4px',
                                    padding: '0.4rem 0.55rem',
                                    backgroundColor: isSelected ? '#0d6efd' : 'transparent',
                                    color: isSelected ? '#fff' : '#212529'
                                  }}
                                >
                                  <div className="d-flex align-items-center justify-content-between gap-2">
                                    <span className="d-flex align-items-center gap-2">
                                      <span style={{ width: '14px', display: 'inline-flex', justifyContent: 'center' }}>
                                        {renderScheduledActivityIcon(option.type)}
                                      </span>
                                      <span>{option.label}</span>
                                    </span>
                                    {isSelected && <Check size={13} />}
                                  </div>
                                </Dropdown.Item>
                              );
                            })}
                          </Dropdown.Menu>
                        </Dropdown>
                        <Form.Control
                          size="sm"
                          value={scheduledActivityText}
                          onChange={(e) => setScheduledActivityText(e.target.value)}
                          style={{
                            fontSize: '0.7rem',
                            borderTopLeftRadius: 0,
                            borderBottomLeftRadius: 0,
                            marginLeft: '-1px'
                          }}
                        />
                      </div>
                      <div className="d-flex gap-1 mt-2">
                        <Button
                          variant="outline-secondary"
                          size="sm"
                          style={{ fontSize: '0.65rem' }}
                          onClick={() => handleQuickScheduleActivity('in1h')}
                          disabled={isCreatingMeeting}
                        >
                          In 1h
                        </Button>
                        <Button
                          variant="outline-secondary"
                          size="sm"
                          style={{ fontSize: '0.65rem' }}
                          onClick={() => handleQuickScheduleActivity('in3h')}
                          disabled={isCreatingMeeting}
                        >
                          In 3h
                        </Button>
                        <Button
                          variant="outline-secondary"
                          size="sm"
                          style={{ fontSize: '0.65rem' }}
                          onClick={() => handleQuickScheduleActivity('tomorrow')}
                          disabled={isCreatingMeeting}
                        >
                          Tomorrow
                        </Button>
                        <Button
                          variant="outline-secondary"
                          size="sm"
                          style={{ fontSize: '0.65rem' }}
                          onClick={() => handleQuickScheduleActivity('nextWeek')}
                          disabled={isCreatingMeeting}
                        >
                          Next week
                        </Button>
                        <Button
                          variant="outline-secondary"
                          size="sm"
                          style={{ fontSize: '0.65rem' }}
                          onClick={() => handleQuickScheduleActivity('other')}
                          disabled={isCreatingMeeting}
                        >
                          + Other
                        </Button>
                      </div>
                    </div>
                    <div>
                      <h6 className="small mb-2" style={{ fontSize: '0.7rem', fontWeight: 'bold' }}>History</h6>
                      <ListGroup variant="flush">
                        {notes.map((note) => (
                          <ListGroup.Item
                            key={note.id}
                            className="px-0 py-2"
                            style={{
                              fontSize: '0.75rem',
                              backgroundColor: '#fff9e6'
                            }}
                          >
                            <div className="d-flex align-items-start gap-2">
                              <FileText size={14} className="mt-1" style={{ color: '#6c757d' }} />
                              <div className="flex-grow-1">
                                <div
                                  dangerouslySetInnerHTML={{ __html: note.content }}
                                  style={{ fontSize: '0.75rem', marginBottom: '0.25rem' }}
                                />
                                <div className="text-muted small" style={{ fontSize: '0.7rem' }}>
                                  {formatDate(note.timestamp)} · {note.author}
                                </div>
                              </div>
                              <div className="d-flex gap-1">
                                <Button
                                  variant="link"
                                  size="sm"
                                  className="p-0"
                                  style={{ color: note.pinned ? '#0d6efd' : '#6c757d' }}
                                  onClick={() => handleTogglePinNote(note.id)}
                                  title={note.pinned ? 'Unpin note' : 'Pin note to Focus'}
                                >
                                  <Pin size={12} />
                                </Button>
                                <Dropdown align="end">
                                  <Dropdown.Toggle
                                    as={IconOnlyDropdownToggle}
                                    id={`client-note-actions-${note.id}`}
                                    style={{
                                      width: '20px',
                                      height: '20px',
                                      border: 'none',
                                      backgroundColor: 'transparent',
                                      color: '#6c757d'
                                    }}
                                  >
                                    <ThreeDotsVertical size={12} />
                                  </Dropdown.Toggle>
                                  <Dropdown.Menu>
                                    <Dropdown.Item
                                      onClick={() => handleDeleteNote(note.id)}
                                      style={{ fontSize: '0.75rem', color: '#dc3545' }}
                                    >
                                      Delete note
                                    </Dropdown.Item>
                                  </Dropdown.Menu>
                                </Dropdown>
                              </div>
                            </div>
                          </ListGroup.Item>
                        ))}
                        <ListGroup.Item className="px-0 py-2" style={{ fontSize: '0.75rem' }}>
                          <div className="text-muted small" style={{ fontSize: '0.7rem' }}>Client created</div>
                          <div style={{ fontSize: '0.75rem' }}>{selectedClient.createdAt ? formatDate(selectedClient.createdAt) : 'Unknown'} · PhiLL Kross</div>
                        </ListGroup.Item>
                      </ListGroup>
                    </div>
                  </Tab>
                  <Tab eventKey="activity" title="Activity">
                    <div style={{ position: 'relative', display: 'flex', gap: '1rem', alignItems: 'flex-start' }}>
                      <div style={{ flex: '1 1 auto', minWidth: 0 }}>
                        <div className="mb-3">
                          <Form.Control
                            size="sm"
                            value={activitySchedulerForm.title}
                            onChange={(e) => handleActivitySchedulerFieldChange('title', e.target.value)}
                            placeholder="Activity title"
                            style={{ fontSize: '0.95rem', padding: '0.4rem 0.5rem', marginBottom: '0.5rem' }}
                          />
                          <div className="d-flex gap-1 flex-wrap">
                            {SCHEDULE_ACTIVITY_OPTIONS.map((option) => (
                              <button
                                key={option.type}
                                type="button"
                                onClick={() => handleSelectDetailedActivityType(option.type)}
                                style={{
                                  width: 34,
                                  height: 34,
                                  borderRadius: 6,
                                  border: '1px solid #dee2e6',
                                  backgroundColor: scheduledActivityType === option.type ? '#0d6efd' : '#fff',
                                  color: scheduledActivityType === option.type ? '#fff' : '#6c757d',
                                  display: 'flex',
                                  alignItems: 'center',
                                  justifyContent: 'center',
                                  cursor: 'pointer',
                                }}
                                title={option.label}
                              >
                                {renderScheduledActivityIcon(option.type)}
                              </button>
                            ))}
                          </div>
                        </div>

                        <div className="d-flex align-items-center gap-2 mb-3 flex-wrap">
                          <Clock size={16} style={{ color: '#6c757d', flexShrink: 0 }} />
                          <Form.Control
                            type="date"
                            size="sm"
                            value={activitySchedulerForm.startTime ? activitySchedulerForm.startTime.slice(0, 10) : ''}
                            onChange={(e) => {
                              const dateValue = e.target.value;
                              const startTime = activitySchedulerForm.startTime ? activitySchedulerForm.startTime.slice(11, 16) : '12:00';
                              const endTime = activitySchedulerForm.endTime ? activitySchedulerForm.endTime.slice(11, 16) : '13:00';
                              setActivitySchedulerForm((prev) => ({
                                ...prev,
                                startTime: `${dateValue}T${startTime}`,
                                endTime: `${dateValue}T${endTime}`,
                              }));
                            }}
                            style={{ width: 140, fontSize: '0.75rem' }}
                          />
                          <Form.Control
                            type="time"
                            size="sm"
                            value={activitySchedulerForm.startTime ? activitySchedulerForm.startTime.slice(11, 16) : ''}
                            onChange={(e) => {
                              const dateValue = activitySchedulerForm.startTime
                                ? activitySchedulerForm.startTime.slice(0, 10)
                                : (getDateKeyInTimezone(new Date(), tenantTimezone) || '');
                              handleActivitySchedulerFieldChange('startTime', `${dateValue}T${e.target.value}`);
                            }}
                            style={{ width: 100, fontSize: '0.75rem' }}
                          />
                          <span style={{ color: '#6c757d' }}>-</span>
                          <Form.Control
                            type="time"
                            size="sm"
                            value={activitySchedulerForm.endTime ? activitySchedulerForm.endTime.slice(11, 16) : ''}
                            onChange={(e) => {
                              const dateValue = activitySchedulerForm.endTime
                                ? activitySchedulerForm.endTime.slice(0, 10)
                                : activitySchedulerForm.startTime?.slice(0, 10)
                                  || (getDateKeyInTimezone(new Date(), tenantTimezone) || '');
                              handleActivitySchedulerFieldChange('endTime', `${dateValue}T${e.target.value}`);
                            }}
                            style={{ width: 100, fontSize: '0.75rem' }}
                          />
                        </div>

                        <div className="d-flex align-items-center gap-2 mb-2">
                          <Calendar size={16} style={{ color: '#6c757d', flexShrink: 0 }} />
                          <Form.Select
                            size="sm"
                            value={activitySchedulerForm.priority}
                            onChange={(e) => handleActivitySchedulerFieldChange('priority', e.target.value)}
                            style={{ width: 140, fontSize: '0.75rem' }}
                          >
                            <option value="low">Low</option>
                            <option value="normal">Normal</option>
                            <option value="high">High</option>
                          </Form.Select>
                        </div>

                        <div className="d-flex align-items-center gap-2 mb-2">
                          <Briefcase size={16} style={{ color: '#6c757d', flexShrink: 0 }} />
                          <Form.Select
                            size="sm"
                            value={activitySchedulerForm.purposeTypeId}
                            onChange={(e) => handleActivitySchedulerFieldChange('purposeTypeId', e.target.value)}
                            style={{ flex: 1, maxWidth: 280, fontSize: '0.75rem' }}
                          >
                            {meetingPurposeOptions.map((option) => (
                              <option key={option.id} value={option.id}>
                                {option.name}
                              </option>
                            ))}
                          </Form.Select>
                        </div>

                        <div className="d-flex align-items-center gap-2 mb-2">
                          <Link45deg size={16} style={{ color: '#6c757d', flexShrink: 0 }} />
                          <Form.Control
                            size="sm"
                            value={activitySchedulerForm.location}
                            onChange={(e) => handleActivitySchedulerFieldChange('location', e.target.value)}
                            placeholder="Add location"
                            style={{ flex: 1, fontSize: '0.75rem' }}
                          />
                        </div>

                        <div className="d-flex align-items-center gap-2 mb-2">
                          <Paperclip size={16} style={{ color: '#6c757d', flexShrink: 0 }} />
                          <Form.Select
                            size="sm"
                            value={activitySchedulerForm.videoCallProvider}
                            onChange={(e) => {
                              const provider = e.target.value as '' | 'zoom' | 'teams' | 'google-meet';
                              setActivitySchedulerForm((prev) => ({
                                ...prev,
                                videoCallProvider: provider,
                                meetingType: provider ? 'video' : ((scheduledActivityType === 'call' || scheduledActivityType === 'text') ? 'phone' : 'in-person'),
                                meetingUrl: provider ? prev.meetingUrl : '',
                              }));
                            }}
                            style={{ flex: 1, maxWidth: 260, fontSize: '0.75rem' }}
                          >
                            <option value="">Install video call integration</option>
                            <option value="zoom">Zoom meeting</option>
                            <option value="teams">Microsoft Teams meeting</option>
                            <option value="google-meet">Google Meet meeting</option>
                          </Form.Select>
                        </div>

                        {activitySchedulerForm.videoCallProvider && (
                          <div className="d-flex align-items-center gap-2 mb-2" style={{ marginLeft: 24 }}>
                            <Form.Control
                              size="sm"
                              value={activitySchedulerForm.meetingUrl}
                              onChange={(e) => handleActivitySchedulerFieldChange('meetingUrl', e.target.value)}
                              placeholder={
                                activitySchedulerForm.videoCallProvider === 'google-meet'
                                  ? 'Paste Google Meet link here'
                                  : activitySchedulerForm.videoCallProvider === 'zoom'
                                    ? 'Paste Zoom link here'
                                    : 'Paste Teams link here'
                              }
                              style={{ flex: 1, fontSize: '0.75rem' }}
                            />
                          </div>
                        )}

                        <div className="d-flex align-items-center gap-2 mb-3">
                          <Tag size={16} style={{ color: '#6c757d', flexShrink: 0 }} />
                          <Form.Select
                            size="sm"
                            value={activitySchedulerForm.freeBusy}
                            onChange={(e) => handleActivitySchedulerFieldChange('freeBusy', e.target.value)}
                            style={{ width: 100, fontSize: '0.75rem' }}
                          >
                            <option value="free">Free</option>
                            <option value="busy">Busy</option>
                          </Form.Select>
                          <OverlayTrigger placement="top" overlay={<Tooltip>Controls whether this activity blocks your schedule.</Tooltip>}>
                            <span style={{ color: '#6c757d', cursor: 'pointer', display: 'inline-flex' }}>
                              <InfoCircle size={16} />
                            </span>
                          </OverlayTrigger>
                        </div>

                        <div className="mb-3">
                          <div className="d-flex align-items-center gap-2 mb-1">
                            <FileText size={16} style={{ color: '#6c757d' }} />
                            <span style={{ fontSize: '0.75rem', fontWeight: 500 }}>Notes</span>
                          </div>
                          <Form.Control
                            as="textarea"
                            rows={4}
                            size="sm"
                            value={activitySchedulerForm.notes}
                            onChange={(e) => handleActivitySchedulerFieldChange('notes', e.target.value)}
                            placeholder="Notes are visible within the app, but not to event guests."
                            style={{ fontSize: '0.75rem', padding: '0.4rem', backgroundColor: '#fffef0', border: '1px solid #dee2e6' }}
                          />
                          <div style={{ fontSize: '0.65rem', color: '#6c757d', marginTop: '0.25rem' }}>
                            Notes are visible within the app, but not to event guests.
                          </div>
                        </div>

                        <div className="mb-2">
                          <div className="d-flex align-items-center gap-2 mb-1">
                            <Person size={16} style={{ color: '#6c757d', flexShrink: 0 }} />
                            <Form.Control size="sm" value="You" readOnly style={{ flex: 1, fontSize: '0.75rem', backgroundColor: '#f8f9fa' }} />
                          </div>

                          <div className="d-flex align-items-center gap-2 mb-1" style={{ position: 'relative' }}>
                            <CurrencyDollar size={16} style={{ color: '#6c757d', flexShrink: 0 }} />
                            <div style={{ flex: 1, position: 'relative' }}>
                              <Form.Control
                                size="sm"
                                value={activityDealLeadDisplayLabel || activityDealLeadSearch}
                                onChange={(e) => {
                                  setActivityDealLeadSearch(e.target.value);
                                  setShowActivityDealLeadDropdown(true);
                                  if (activitySchedulerForm.dealId || activitySchedulerForm.leadId) {
                                    setActivitySchedulerForm((prev) => ({ ...prev, dealId: '', leadId: '' }));
                                  }
                                }}
                                onFocus={() => setShowActivityDealLeadDropdown(true)}
                                onBlur={() => setTimeout(() => setShowActivityDealLeadDropdown(false), 200)}
                                placeholder="Deal or Lead"
                                style={{ width: '100%', fontSize: '0.75rem' }}
                              />
                              {(activitySchedulerForm.dealId || activitySchedulerForm.leadId) && (
                                <button
                                  type="button"
                                  onClick={() => setActivitySchedulerForm((prev) => ({ ...prev, dealId: '', leadId: '' }))}
                                  style={{ position: 'absolute', right: 8, top: '50%', transform: 'translateY(-50%)', background: 'none', border: 'none', padding: 0, cursor: 'pointer', color: '#6c757d' }}
                                >
                                  <X size={12} />
                                </button>
                              )}
                              {showActivityDealLeadDropdown && (
                                <div style={{ position: 'absolute', top: '100%', left: 0, right: 0, zIndex: 10, backgroundColor: '#fff', border: '1px solid #dee2e6', borderRadius: 4, boxShadow: '0 4px 12px rgba(0,0,0,0.15)', maxHeight: 200, overflowY: 'auto' }}>
                                  {filteredActivityDealsAndLeads.length === 0 ? (
                                    <div style={{ padding: '0.5rem 0.75rem', fontSize: '0.75rem', color: '#6c757d' }}>No deals or leads found</div>
                                  ) : (
                                    filteredActivityDealsAndLeads.map((item) => (
                                      <div
                                        key={`${item.type}-${item.id}`}
                                        onClick={() => {
                                          if (item.type === 'deal') {
                                            setActivitySchedulerForm((prev) => ({ ...prev, dealId: item.id, leadId: '' }));
                                          } else {
                                            setActivitySchedulerForm((prev) => ({ ...prev, leadId: item.id, dealId: '' }));
                                          }
                                          setActivityDealLeadSearch('');
                                          setShowActivityDealLeadDropdown(false);
                                        }}
                                        style={{ padding: '0.4rem 0.75rem', fontSize: '0.75rem', cursor: 'pointer', borderBottom: '1px solid #f0f0f0' }}
                                        onMouseDown={(e) => e.preventDefault()}
                                      >
                                        {item.label}
                                      </div>
                                    ))
                                  )}
                                </div>
                              )}
                            </div>
                          </div>

                          <div className="d-flex align-items-center gap-2 mb-1" style={{ position: 'relative' }}>
                            <PeopleFill size={16} style={{ color: '#6c757d', flexShrink: 0 }} />
                            <div style={{ flex: 1, position: 'relative' }}>
                              <Form.Control
                                size="sm"
                                value={activityContactDisplayLabel || activityPeopleSearch}
                                onChange={(e) => {
                                  setActivityPeopleSearch(e.target.value);
                                  setShowActivityPeopleDropdown(true);
                                  if (activitySchedulerForm.contactId) setActivitySchedulerForm((prev) => ({ ...prev, contactId: '' }));
                                }}
                                onFocus={() => setShowActivityPeopleDropdown(true)}
                                onBlur={() => setTimeout(() => setShowActivityPeopleDropdown(false), 200)}
                                placeholder="People"
                                style={{ width: '100%', fontSize: '0.75rem' }}
                              />
                              {activitySchedulerForm.contactId && (
                                <button
                                  type="button"
                                  onClick={() => setActivitySchedulerForm((prev) => ({ ...prev, contactId: '' }))}
                                  style={{ position: 'absolute', right: 8, top: '50%', transform: 'translateY(-50%)', background: 'none', border: 'none', padding: 0, cursor: 'pointer', color: '#6c757d' }}
                                >
                                  <X size={12} />
                                </button>
                              )}
                              {showActivityPeopleDropdown && (
                                <div style={{ position: 'absolute', top: '100%', left: 0, right: 0, zIndex: 10, backgroundColor: '#fff', border: '1px solid #dee2e6', borderRadius: 4, boxShadow: '0 4px 12px rgba(0,0,0,0.15)', maxHeight: 200, overflowY: 'auto' }}>
                                  {filteredActivityContacts.length === 0 ? (
                                    <div style={{ padding: '0.5rem 0.75rem', fontSize: '0.75rem', color: '#6c757d' }}>No contacts found</div>
                                  ) : (
                                    filteredActivityContacts.map((item) => (
                                      <div
                                        key={item.id}
                                        onClick={() => {
                                          setActivitySchedulerForm((prev) => ({ ...prev, contactId: item.id }));
                                          setActivityPeopleSearch('');
                                          setShowActivityPeopleDropdown(false);
                                        }}
                                        style={{ padding: '0.4rem 0.75rem', fontSize: '0.75rem', cursor: 'pointer', borderBottom: '1px solid #f0f0f0' }}
                                        onMouseDown={(e) => e.preventDefault()}
                                      >
                                        {item.label}
                                      </div>
                                    ))
                                  )}
                                </div>
                              )}
                            </div>
                          </div>

                          <div className="d-flex align-items-center gap-2" style={{ position: 'relative' }}>
                            <BuildingFill size={16} style={{ color: '#6c757d', flexShrink: 0 }} />
                            <div style={{ flex: 1, position: 'relative' }}>
                              <Form.Control
                                size="sm"
                                value={activityOrganizationDisplayLabel || activityOrganizationSearch}
                                onChange={(e) => {
                                  setActivityOrganizationSearch(e.target.value);
                                  setShowActivityOrganizationDropdown(true);
                                  if (activitySchedulerForm.clientId) setActivitySchedulerForm((prev) => ({ ...prev, clientId: '' }));
                                }}
                                onFocus={() => setShowActivityOrganizationDropdown(true)}
                                onBlur={() => setTimeout(() => setShowActivityOrganizationDropdown(false), 200)}
                                placeholder="Organization"
                                style={{ width: '100%', fontSize: '0.75rem' }}
                              />
                              {activitySchedulerForm.clientId && (
                                <button
                                  type="button"
                                  onClick={() => setActivitySchedulerForm((prev) => ({ ...prev, clientId: '' }))}
                                  style={{ position: 'absolute', right: 8, top: '50%', transform: 'translateY(-50%)', background: 'none', border: 'none', padding: 0, cursor: 'pointer', color: '#6c757d' }}
                                >
                                  <X size={12} />
                                </button>
                              )}
                              {showActivityOrganizationDropdown && (
                                <div style={{ position: 'absolute', top: '100%', left: 0, right: 0, zIndex: 10, backgroundColor: '#fff', border: '1px solid #dee2e6', borderRadius: 4, boxShadow: '0 4px 12px rgba(0,0,0,0.15)', maxHeight: 200, overflowY: 'auto' }}>
                                  {filteredActivityOrganizations.length === 0 ? (
                                    <div style={{ padding: '0.5rem 0.75rem', fontSize: '0.75rem', color: '#6c757d' }}>No organizations found</div>
                                  ) : (
                                    filteredActivityOrganizations.map((item) => (
                                      <div
                                        key={item.id}
                                        onClick={() => {
                                          setActivitySchedulerForm((prev) => ({ ...prev, clientId: item.id }));
                                          setActivityOrganizationSearch('');
                                          setShowActivityOrganizationDropdown(false);
                                        }}
                                        style={{ padding: '0.4rem 0.75rem', fontSize: '0.75rem', cursor: 'pointer', borderBottom: '1px solid #f0f0f0' }}
                                        onMouseDown={(e) => e.preventDefault()}
                                      >
                                        {item.label}
                                      </div>
                                    ))
                                  )}
                                </div>
                              )}
                            </div>
                          </div>
                        </div>
                      </div>

                      <OverlayTrigger
                        placement="top"
                        trigger={[]}
                        show={showCalendarToggleTooltip}
                        overlay={
                          <Tooltip id="client-activity-calendar-toggle-tooltip" style={{ pointerEvents: 'none' }}>
                            {showActivityCalendar ? 'Hide calendar' : 'Show calendar'}
                          </Tooltip>
                        }
                      >
                        <button
                          type="button"
                          onClick={(event) => {
                            event.currentTarget.blur();
                            setShowCalendarToggleTooltip(false);
                            setShowActivityCalendar((prev) => !prev);
                          }}
                          onMouseEnter={() => setShowCalendarToggleTooltip(true)}
                          onMouseLeave={() => setShowCalendarToggleTooltip(false)}
                          onFocus={() => setShowCalendarToggleTooltip(true)}
                          onBlur={() => setShowCalendarToggleTooltip(false)}
                          aria-label={showActivityCalendar ? 'Hide calendar' : 'Show calendar'}
                          style={{
                            position: 'absolute',
                            right: showActivityCalendar ? ACTIVITY_CALENDAR_PANEL_WIDTH : 0,
                            top: 120,
                            transform: 'translate(50%, -50%)',
                            width: 42,
                            height: 42,
                            borderRadius: '999px',
                            border: '1px solid #ced4da',
                            backgroundColor: '#f8f9fa',
                            color: '#495057',
                            display: 'inline-flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            cursor: 'pointer',
                            zIndex: 3,
                            transition: 'right 0.25s ease',
                            boxShadow: '0 2px 6px rgba(0,0,0,0.08)',
                          }}
                        >
                          {showActivityCalendar ? <ArrowRight size={16} /> : <ArrowLeft size={16} />}
                        </button>
                      </OverlayTrigger>

                      <div
                        style={{
                          width: showActivityCalendar ? ACTIVITY_CALENDAR_PANEL_WIDTH : 0,
                          maxWidth: '100%',
                          flex: '0 0 auto',
                          overflow: 'hidden',
                          opacity: showActivityCalendar ? 1 : 0,
                          transition: 'width 0.25s ease, opacity 0.2s ease',
                          borderLeft: showActivityCalendar ? '1px solid #dee2e6' : 'none',
                          paddingLeft: 0,
                        }}
                      >
                        <div
                          style={{
                            width: '100%',
                            maxWidth: '100%',
                            boxSizing: 'border-box',
                            paddingLeft: '0.5rem',
                            paddingRight: '0.35rem',
                            pointerEvents: showActivityCalendar ? 'auto' : 'none',
                          }}
                        >
                          <div className="d-flex align-items-center justify-content-between mb-2">
                            <div style={{ fontSize: '0.85rem', fontWeight: 600 }}>
                              {toLocaleDateStringInTimezone(
                                getActivitySchedulerDisplayDate(),
                                'en-US',
                                { weekday: 'long', month: 'long', day: 'numeric' },
                                tenantTimezone
                              )}
                            </div>
                            <div className="d-flex align-items-center gap-1">
                              <button
                                type="button"
                                onClick={handleActivitySchedulerPrevDay}
                                style={{ border: 'none', background: 'none', padding: '0.15rem', cursor: 'pointer', color: '#6c757d' }}
                                title="Previous day"
                              >
                                <ArrowLeft size={14} />
                              </button>
                              <button
                                type="button"
                                onClick={handleActivitySchedulerNextDay}
                                style={{ border: 'none', background: 'none', padding: '0.15rem', cursor: 'pointer', color: '#6c757d' }}
                                title="Next day"
                              >
                                <ArrowRight size={14} />
                              </button>
                              <Button
                                variant="outline-secondary"
                                size="sm"
                                onClick={handleActivitySchedulerToday}
                                style={{ fontSize: '0.65rem', padding: '0.12rem 0.35rem', whiteSpace: 'nowrap' }}
                              >
                                Today
                              </Button>
                            </div>
                          </div>

                          <div style={{ display: 'flex', overflow: 'hidden', borderRadius: 4, border: '1px solid #dee2e6' }}>
                            <div style={{ width: 44, flexShrink: 0, backgroundColor: '#f8f9fa', padding: '0.25rem 0' }}>
                              {Array.from({ length: ACTIVITY_CALENDAR_VISIBLE_HOURS }, (_, i) => activityCalendarStartHour + i).map((hour) => (
                                <div
                                  key={`activity-hour-label-${hour}`}
                                  style={{
                                    height: ACTIVITY_CALENDAR_SLOT_HEIGHT,
                                    fontSize: '0.65rem',
                                    color: '#6c757d',
                                    paddingLeft: '0.35rem',
                                    display: 'flex',
                                    alignItems: 'center',
                                  }}
                                >
                                  {formatActivityCalendarHourLabel(hour)}
                                </div>
                              ))}
                            </div>
                            <div
                              onClick={handleActivityCalendarGridClick}
                              style={{ flex: 1, position: 'relative', minHeight: ACTIVITY_CALENDAR_VISIBLE_HOURS * ACTIVITY_CALENDAR_SLOT_HEIGHT, cursor: 'pointer' }}
                            >
                              {Array.from({ length: ACTIVITY_CALENDAR_VISIBLE_HOURS }, (_, i) => activityCalendarStartHour + i).map((hour) => (
                                <div
                                  key={`activity-hour-grid-${hour}`}
                                  style={{
                                    height: ACTIVITY_CALENDAR_SLOT_HEIGHT,
                                    borderBottom: '1px solid #eee',
                                  }}
                                />
                              ))}
                              {(() => {
                                const displayDate = getActivitySchedulerDisplayDate();
                                const now = new Date(activityNowTick);
                                const isTodayLine =
                                  displayDate.getDate() === now.getDate()
                                  && displayDate.getMonth() === now.getMonth()
                                  && displayDate.getFullYear() === now.getFullYear();
                                const nowHour = now.getHours() + now.getMinutes() / 60;
                                const visibleStartHour = activityCalendarStartHour;
                                const visibleEndHour = activityCalendarStartHour + ACTIVITY_CALENDAR_VISIBLE_HOURS;
                                const topPx = isTodayLine && nowHour >= visibleStartHour && nowHour < visibleEndHour
                                  ? (nowHour - visibleStartHour) * ACTIVITY_CALENDAR_SLOT_HEIGHT
                                  : -999;
                                return (
                                  <div
                                    style={{
                                      position: 'absolute',
                                      left: 0,
                                      right: 0,
                                      top: topPx,
                                      height: 2,
                                      backgroundColor: '#dc3545',
                                      display: 'flex',
                                      alignItems: 'center',
                                      zIndex: 2,
                                    }}
                                  >
                                    <span
                                      style={{
                                        fontSize: '0.6rem',
                                        color: '#dc3545',
                                        backgroundColor: '#fff',
                                        padding: '0 0.2rem',
                                        marginLeft: 4,
                                      }}
                                    >
                                      {toLocaleTimeStringInTimezone(
                                        now,
                                        'en-US',
                                        { hour: 'numeric', minute: '2-digit' },
                                        tenantTimezone
                                      )}
                                    </span>
                                    <span
                                      style={{
                                        width: 6,
                                        height: 6,
                                        borderRadius: '50%',
                                        backgroundColor: '#dc3545',
                                        marginLeft: 2,
                                      }}
                                    />
                                  </div>
                                );
                              })()}
                              {activitySchedulerForm.startTime && activitySchedulerForm.endTime && (() => {
                                const start = new Date(activitySchedulerForm.startTime);
                                const end = new Date(activitySchedulerForm.endTime);
                                const displayDate = getActivitySchedulerDisplayDate();
                                const sameDay =
                                  start.getDate() === displayDate.getDate()
                                  && start.getMonth() === displayDate.getMonth()
                                  && start.getFullYear() === displayDate.getFullYear();
                                if (!sameDay || Number.isNaN(start.getTime()) || Number.isNaN(end.getTime())) return null;
                                let startHour = start.getHours() + start.getMinutes() / 60;
                                let endHour = end.getHours() + end.getMinutes() / 60;
                                if (endHour < startHour) endHour += 24;
                                const visibleStartHour = activityCalendarStartHour;
                                const visibleEndHour = activityCalendarStartHour + ACTIVITY_CALENDAR_VISIBLE_HOURS;
                                if (startHour >= visibleEndHour || endHour <= visibleStartHour) return null;
                                const top = (Math.max(startHour, visibleStartHour) - visibleStartHour) * ACTIVITY_CALENDAR_SLOT_HEIGHT;
                                const height = Math.max(
                                  (Math.min(endHour, visibleEndHour) - Math.max(startHour, visibleStartHour)) * ACTIVITY_CALENDAR_SLOT_HEIGHT,
                                  24
                                );
                                return (
                                  <div
                                    className="rounded"
                                    style={{
                                      position: 'absolute',
                                      left: 4,
                                      right: 4,
                                      top,
                                      height: Math.max(height, 24),
                                      backgroundColor: '#0d6efd',
                                      color: '#fff',
                                      display: 'flex',
                                      alignItems: 'center',
                                      gap: 6,
                                      padding: '0 0.35rem',
                                      fontSize: '0.7rem',
                                      fontWeight: 500,
                                      zIndex: 1,
                                    }}
                                  >
                                    {renderScheduledActivityIcon(scheduledActivityType)}
                                    {activitySchedulerForm.title || 'Activity'}
                                  </div>
                                );
                              })()}
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>

                    <div className="d-flex justify-content-between align-items-center mt-3 pt-2" style={{ borderTop: '1px solid #dee2e6' }}>
                      <Form.Check
                        type="checkbox"
                        id="client-activity-mark-done"
                        label="Mark as done"
                        checked={activitySchedulerForm.markAsDone}
                        onChange={(e) => handleActivitySchedulerFieldChange('markAsDone', e.target.checked)}
                        style={{ fontSize: '0.75rem', marginBottom: 0 }}
                      />
                      <div className="d-flex gap-2">
                        <Button variant="secondary" size="sm" onClick={handleCancelDetailedActivity}>
                          Cancel
                        </Button>
                        <Button
                          variant="primary"
                          size="sm"
                          onClick={handleSaveDetailedActivity}
                          disabled={isCreatingMeeting || !activitySchedulerForm.title || !activitySchedulerForm.startTime || !activitySchedulerForm.endTime}
                        >
                          {isCreatingMeeting ? <Spinner size="sm" animation="border" /> : 'Save'}
                        </Button>
                      </div>
                    </div>

                    <div className="mt-3">
                      <h6 className="small mb-2" style={{ fontSize: '0.7rem', fontWeight: 'bold' }}>Timeline</h6>
                      {clientActivityTimeline.length === 0 ? (
                        <p className="text-muted small" style={{ fontSize: '0.7rem' }}>No activities yet for this client.</p>
                      ) : (
                        <ListGroup variant="flush">
                          {clientActivityTimeline.map((meeting) => {
                            const activityType = getScheduledActivityTypeFromMeeting(meeting);
                            const meetingStartMs = new Date(meeting.startTime).getTime();
                            const isUpcoming = !Number.isNaN(meetingStartMs) && meetingStartMs >= Date.now();
                            return (
                              <ListGroup.Item key={`activity-entry-${meeting.id}`} className="px-0 py-2" style={{ fontSize: '0.75rem' }}>
                                <div className="d-flex align-items-start gap-2">
                                  <div
                                    className="d-inline-flex align-items-center justify-content-center border"
                                    style={{
                                      width: '30px',
                                      height: '30px',
                                      borderRadius: '999px',
                                      backgroundColor: '#f8f9fa',
                                      color: '#495057',
                                      marginTop: '2px'
                                    }}
                                  >
                                    {renderScheduledActivityIcon(activityType)}
                                  </div>
                                  <div className="flex-grow-1">
                                    <div className="d-flex align-items-center justify-content-between gap-2">
                                      <div className="fw-semibold" style={{ fontSize: '0.75rem' }}>
                                        {meeting.title || SCHEDULE_ACTIVITY_LABELS[activityType]}
                                      </div>
                                      <Badge bg={isUpcoming ? 'primary' : 'secondary'} style={{ fontSize: '0.6rem' }}>
                                        {isUpcoming ? 'Upcoming' : 'Past'}
                                      </Badge>
                                    </div>
                                    <div className="text-muted small mt-1" style={{ fontSize: '0.7rem' }}>
                                      {formatDate(meeting.startTime)}
                                    </div>
                                  </div>
                                </div>
                              </ListGroup.Item>
                            );
                          })}
                        </ListGroup>
                      )}
                    </div>
                  </Tab>
                  <Tab eventKey="email" title="Email">
                    <div className="d-flex align-items-center justify-content-between mb-2">
                      <h6 className="small mb-0" style={{ fontSize: '0.7rem', fontWeight: 'bold' }}>
                        Linked threads
                      </h6>
                      <Button
                        variant="outline-secondary"
                        size="sm"
                        onClick={() => refetchClientEmailThreads()}
                        disabled={!clientEmailAddress || isRefreshingClientEmailThreads}
                        style={{ fontSize: '0.65rem', padding: '0.2rem 0.5rem' }}
                      >
                        {isRefreshingClientEmailThreads ? <Spinner size="sm" animation="border" /> : 'Refresh'}
                      </Button>
                    </div>
                    {!clientEmailAddress ? (
                      <p className="text-muted small" style={{ fontSize: '0.7rem' }}>
                        Add an email address to this client to link Unibox threads.
                      </p>
                    ) : isLoadingClientEmailThreads ? (
                      <div className="d-flex align-items-center gap-2 text-muted small" style={{ fontSize: '0.7rem' }}>
                        <Spinner size="sm" animation="border" />
                        Loading email threads...
                      </div>
                    ) : clientEmailThreadsError ? (
                      <Alert variant="danger" className="py-2 px-3 mb-0" style={{ fontSize: '0.7rem' }}>
                        Failed to load email threads.
                      </Alert>
                    ) : clientEmailThreads.length === 0 ? (
                      <p className="text-muted small mb-0" style={{ fontSize: '0.7rem' }}>
                        No email threads found for {clientEmailAddress}.
                      </p>
                    ) : (
                      <ListGroup variant="flush">
                        {clientEmailThreads.map((thread) => (
                          <ListGroup.Item key={thread.id} className="px-0 py-2" style={{ fontSize: '0.75rem' }}>
                            <div className="d-flex align-items-start gap-2">
                              <Envelope size={14} className="mt-1" style={{ color: '#6c757d' }} />
                              <div className="flex-grow-1">
                                <div className="d-flex align-items-start justify-content-between gap-2">
                                  <div className="fw-semibold" style={{ fontSize: '0.75rem' }}>
                                    {thread.subject}
                                  </div>
                                  <div className="d-flex align-items-center gap-1">
                                    {thread.unreadCount > 0 && (
                                      <Badge bg="primary" style={{ fontSize: '0.6rem' }}>
                                        {thread.unreadCount} unread
                                      </Badge>
                                    )}
                                    {thread.messageCount > 1 && (
                                      <Badge bg="secondary" style={{ fontSize: '0.6rem' }}>
                                        {thread.messageCount} msgs
                                      </Badge>
                                    )}
                                  </div>
                                </div>
                                <div className="text-muted small mt-1" style={{ fontSize: '0.7rem' }}>
                                  {formatDate(thread.latestDate)} · {thread.latestFrom || 'Unknown sender'}
                                </div>
                                <div className="mt-2 d-flex flex-column gap-2">
                                  {thread.messages.map((message) => (
                                    <div key={`${thread.id}-${message.id}`} className="border-start ps-2">
                                      <div className="text-muted small" style={{ fontSize: '0.67rem' }}>
                                        {formatDate(message.date)} · {message.from || 'Unknown sender'}
                                      </div>
                                      <div className="small mt-1" style={{ fontSize: '0.7rem', lineHeight: 1.35 }}>
                                        {message.preview || 'No preview available.'}
                                      </div>
                                    </div>
                                  ))}
                                </div>
                              </div>
                            </div>
                          </ListGroup.Item>
                        ))}
                      </ListGroup>
                    )}
                  </Tab>
                  <Tab eventKey="files" title="Files">
                    <p className="text-muted small" style={{ fontSize: '0.7rem' }}>Files will appear here.</p>
                  </Tab>
                </Tabs>
              </div>
            </div>
          )}
        </Offcanvas.Body>
      </Offcanvas>

      {/* Customize Columns Modal */}
      <Modal show={showCustomizeColumnsModal} onHide={() => setShowCustomizeColumnsModal(false)}>
        <Modal.Header closeButton>
          <Modal.Title>Customize columns</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <p>Column customization coming soon...</p>
        </Modal.Body>
      </Modal>
      </div>
    </Container>
  );
};

export default Clients;
